<template>
    <div class="w-full h-full overflow-y-auto">
        <!-- Primary column -->
        <section
            aria-labelledby="primary-heading"
            class="w-full flex h-full flex-1 flex-col lg:block lg:order-last lg:max-w-4xl lg:mx-auto xl:max-w-6xl"
        >
            <h1 id="primary-heading" class="sr-only">Flags</h1>

            <zen-loading-indicator v-if="loading.flag" />
            <div v-if="!loading.flag && hasResult" class="p-4 sm:px-6 lg:py-6">
                <nav class="lg:hidden flex" aria-label="Breadcrumb">
                    <ol role="list" class="flex items-center space-x-4">
                        <li>
                            <div>
                                <router-link
                                    :to="{
                                        name: 'ProjectFlagsScreen',
                                        params: {
                                            projectKey:
                                                currentEnvironment.projectKey,
                                            environmentKey:
                                                currentEnvironment.environmentKey,
                                        },
                                    }"
                                    class="text-sm font-medium text-taupe-500 hover:text-taupe-800"
                                >
                                    Back to Flags
                                </router-link>
                            </div>
                        </li>
                    </ol>
                </nav>

                <div class="sm:flex sm:items-center">
                    <div class="sm:flex-auto">
                        <flag-header-tabs
                            :current="tab"
                            :flag="result"
                            :project-ulid="project?.ulid"
                            :environment-ulid="environment?.ulid"
                            @changed="toggleTab"
                        />
                    </div>
                </div>
                <div>
                    <!-- Main content - Target -->
                    <div
                        v-if="tab === 'target'"
                        class="flex-1 xl:overflow-y-auto"
                    >
                        <zen-loading-indicator v-if="!targetTabShown" />
                        <div v-if="targetTabShown" class="space-y-6">
                            <div class="pt-4" data-testid="flag-tab-content">
                                <div class="space-y-6">
                                    <flag-stats-panel
                                        :current-target="currentTarget"
                                        :project-key="
                                            currentEnvironment.projectKey
                                        "
                                        :environment-key="
                                            currentEnvironment.environmentKey
                                        "
                                        :flag="result"
                                    />

                                    <div
                                        v-if="!swapTargetView"
                                        class="rounded-xl border border-taupe-200 bg-white p-5 overflow-hidden"
                                    >
                                        <div
                                            data-testid="target-panel-header"
                                            class="-mx-5 -mt-5 mb-5 bg-taupe-50 border-b border-taupe-200 px-5 py-4 flex items-center justify-between gap-4"
                                        >
                                            <div>
                                                <h2
                                                    class="text-xl font-medium text-taupe-800"
                                                >
                                                    Target
                                                </h2>
                                                <p
                                                    class="mt-1 text-sm text-taupe-500"
                                                >
                                                    The value that will be
                                                    served for this project and
                                                    environment. Unless there
                                                    are context specific rules
                                                    below, this value will be
                                                    served for all requests.
                                                </p>
                                            </div>
                                            <zen-button
                                                v-if="
                                                    !isDraft &&
                                                    !hasDraftTargetUlid &&
                                                    !hasScheduledTarget &&
                                                    can(
                                                        PERMISSIONS.TARGET_UPDATE,
                                                        {
                                                            projectUlid:
                                                                project?.ulid,
                                                            environmentUlid:
                                                                environment?.ulid,
                                                        }
                                                    )
                                                "
                                                data-testid="edit-target-btn"
                                                aria-label="Edit target"
                                                variant="outline-neutral"
                                                icon-only
                                                :processing="
                                                    loading.createDraft
                                                "
                                                @click="createDraft"
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    stroke-width="2"
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                    class="h-4 w-4"
                                                >
                                                    <path
                                                        stroke="none"
                                                        d="M0 0h24v24H0z"
                                                        fill="none"
                                                    />
                                                    <path
                                                        d="M4 20h4l10.5 -10.5a2.828 2.828 0 1 0 -4 -4l-10.5 10.5v4"
                                                    />
                                                    <path d="M13.5 6.5l4 4" />
                                                </svg>
                                            </zen-button>
                                        </div>
                                        <div
                                            v-if="draftChange"
                                            data-testid="draft-changed-elsewhere"
                                            role="status"
                                            class="flex flex-col gap-3 rounded-xl border border-sky-200 bg-sky-50 p-4 mb-3 sm:flex-row sm:items-start sm:justify-between"
                                        >
                                            <div class="space-y-2">
                                                <p
                                                    class="text-sm font-medium text-taupe-800"
                                                >
                                                    {{ draftChange.text }}
                                                </p>
                                                <div
                                                    v-if="
                                                        draftChange.unsaved
                                                            .length > 0
                                                    "
                                                    data-testid="draft-unsaved-edits"
                                                >
                                                    <p
                                                        class="text-sm text-taupe-700"
                                                    >
                                                        Your unsaved edits were
                                                        not applied:
                                                    </p>
                                                    <ul
                                                        class="mt-1 list-disc ps-5 text-sm text-taupe-700"
                                                    >
                                                        <li
                                                            v-for="edit in draftChange.unsaved"
                                                            :key="edit.label"
                                                        >
                                                            <span
                                                                class="font-medium"
                                                                >{{
                                                                    edit.label
                                                                }}:</span
                                                            >
                                                            {{ edit.value }}
                                                        </li>
                                                    </ul>
                                                </div>
                                            </div>
                                            <div class="flex shrink-0 gap-2">
                                                <zen-button
                                                    v-if="
                                                        draftChange.kind ===
                                                        'modified'
                                                    "
                                                    type="button"
                                                    variant="secondary"
                                                    size="small"
                                                    data-testid="reload-changed-draft-btn"
                                                    @click="reloadChangedDraft"
                                                >
                                                    Reload draft
                                                </zen-button>
                                                <zen-button
                                                    type="button"
                                                    variant="outline-primary"
                                                    size="small"
                                                    data-testid="dismiss-draft-change-btn"
                                                    @click="dismissDraftChange"
                                                >
                                                    {{
                                                        draftChange.kind ===
                                                        'modified'
                                                            ? 'Keep editing'
                                                            : 'Dismiss'
                                                    }}
                                                </zen-button>
                                            </div>
                                        </div>
                                        <div
                                            v-if="draftBaseStale && isDraft"
                                            data-testid="draft-based-on-older-live"
                                            role="status"
                                            class="flex flex-col gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 mb-3 sm:flex-row sm:items-center sm:justify-between"
                                        >
                                            <p class="text-sm text-amber-800">
                                                This draft is based on an older
                                                live config. A different target
                                                was published for this flag and
                                                environment after you opened it.
                                            </p>
                                            <zen-button
                                                type="button"
                                                variant="outline-warning"
                                                size="small"
                                                @click="draftBaseStale = false"
                                            >
                                                Dismiss
                                            </zen-button>
                                        </div>
                                        <div
                                            v-if="isDraft"
                                            class="flex items-center p-4 mb-3 rounded-lg bg-amber-50 ring-1 ring-amber-200 text-taupe-800"
                                        >
                                            <svg
                                                class="shrink-0 w-5 h-5 text-amber-500"
                                                aria-hidden="true"
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="currentColor"
                                                viewBox="0 0 20 20"
                                            >
                                                <path
                                                    d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z"
                                                />
                                            </svg>
                                            <span class="sr-only">Info</span>
                                            <div
                                                class="ms-3 text-sm font-medium text-amber-800"
                                            >
                                                You are editing a draft of this
                                                target.
                                            </div>
                                            <div class="ms-auto space-x-2">
                                                <zen-button
                                                    variant="warning"
                                                    @click="closeDraftMode"
                                                >
                                                    Close
                                                </zen-button>
                                                <zen-button
                                                    variant="outline-warning"
                                                    @click="
                                                        toggleDraftTargetDeleteModal
                                                    "
                                                >
                                                    Delete Draft
                                                </zen-button>
                                            </div>
                                        </div>

                                        <div
                                            v-if="
                                                !isDraft && hasDraftTargetUlid
                                            "
                                            class="flex items-center p-4 mb-3 rounded-lg bg-sky-50 ring-1 ring-sky-200 text-taupe-800"
                                        >
                                            <svg
                                                class="shrink-0 w-5 h-5 text-sky-500"
                                                aria-hidden="true"
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="currentColor"
                                                viewBox="0 0 20 20"
                                            >
                                                <path
                                                    d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z"
                                                />
                                            </svg>
                                            <span class="sr-only">Info</span>
                                            <div
                                                class="ms-3 text-sm font-medium text-sky-800"
                                            >
                                                This flag has a draft.
                                            </div>
                                            <div
                                                class="ms-3 text-sm text-sky-700"
                                            >
                                                Continue editing?
                                            </div>
                                            <div class="ms-auto space-x-2">
                                                <zen-button
                                                    variant="primary"
                                                    size="small"
                                                    @click="switchToDraft"
                                                >
                                                    Continue
                                                </zen-button>
                                                <zen-button
                                                    variant="outline-primary"
                                                    size="small"
                                                    @click="
                                                        toggleDraftTargetDeleteModal
                                                    "
                                                >
                                                    Delete
                                                </zen-button>
                                            </div>
                                        </div>

                                        <div
                                            v-if="hasScheduledTarget"
                                            class="flex items-center p-4 mb-3 rounded-lg bg-sky-50 ring-1 ring-sky-200 text-taupe-800"
                                        >
                                            <svg
                                                class="shrink-0 w-5 h-5 text-sky-500"
                                                aria-hidden="true"
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="currentColor"
                                                viewBox="0 0 20 20"
                                            >
                                                <path
                                                    d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z"
                                                />
                                            </svg>
                                            <span class="sr-only">Info</span>
                                            <div
                                                class="ms-3 text-sm font-medium text-sky-800"
                                            >
                                                This flag has a target change
                                                scheduled for
                                                <zen-date-time
                                                    :value="
                                                        scheduledTarget.scheduled_at
                                                    "
                                                    format="short"
                                                />.
                                            </div>
                                            <div class="ms-auto space-x-2">
                                                <zen-button
                                                    variant="primary"
                                                    size="small"
                                                    @click="
                                                        toggleViewScheduleChangeModal
                                                    "
                                                >
                                                    View Details
                                                </zen-button>
                                                <zen-button
                                                    v-if="
                                                        can(
                                                            PERMISSIONS.TARGET_DELETE,
                                                            {
                                                                projectUlid:
                                                                    project?.ulid,
                                                                environmentUlid:
                                                                    environment?.ulid,
                                                            }
                                                        )
                                                    "
                                                    variant="outline-primary"
                                                    size="small"
                                                    @click="
                                                        toggleScheduledTargetDeleteModal
                                                    "
                                                >
                                                    Cancel Change
                                                </zen-button>
                                            </div>
                                        </div>

                                        <div
                                            v-if="isReadOnly"
                                            data-testid="current-value-panel"
                                            class="mt-4 rounded-lg bg-white"
                                        >
                                            <div>
                                                <p
                                                    class="text-xs font-medium uppercase tracking-[0.14em] text-taupe-500"
                                                >
                                                    Published Target
                                                </p>
                                                <p
                                                    class="mt-1 text-sm text-taupe-600"
                                                >
                                                    This environment is
                                                    currently serving the value
                                                    below.
                                                </p>
                                            </div>
                                            <div
                                                class="mt-3 rounded-md border border-taupe-200 bg-taupe-50 px-3 py-3"
                                            >
                                                <div
                                                    data-testid="current-value"
                                                    class="font-mono text-base font-medium text-taupe-900 wrap-anywhere"
                                                >
                                                    {{
                                                        currentValue.display !=
                                                        ''
                                                            ? currentValue.display
                                                            : '(empty)'
                                                    }}
                                                </div>
                                            </div>
                                        </div>

                                        <div
                                            v-if="isDraft"
                                            class="mt-4 py-2"
                                            data-testid="draft-target-picker"
                                        >
                                            <boolean-target-toggle
                                                v-if="result.type == 'boolean'"
                                                @toggle="toggleTarget"
                                                :read-only="isReadOnly"
                                                :label="
                                                    result.name +
                                                    ' target value'
                                                "
                                                :current-value="
                                                    isReadOnly
                                                        ? currentValue
                                                        : modals.selectTarget
                                                              .selected
                                                "
                                                :false-value="falseValue"
                                                :true-value="trueValue"
                                            />

                                            <number-target-toggle
                                                v-if="result.type == 'number'"
                                                @toggle="toggleTarget"
                                                :read-only="isReadOnly"
                                                :current-value="
                                                    isReadOnly
                                                        ? currentValue
                                                        : modals.selectTarget
                                                              .selected
                                                "
                                                :values="values"
                                            />

                                            <string-target-toggle
                                                v-if="result.type == 'string'"
                                                @toggle="toggleTarget"
                                                :read-only="isReadOnly"
                                                :current-value="
                                                    isReadOnly
                                                        ? currentValue
                                                        : modals.selectTarget
                                                              .selected
                                                "
                                                :values="values"
                                            />

                                            <json-target-toggle
                                                v-if="result.type == 'json'"
                                                @toggle="toggleTarget"
                                                :read-only="isReadOnly"
                                                :current-value="
                                                    isReadOnly
                                                        ? currentValue
                                                        : modals.selectTarget
                                                              .selected
                                                "
                                                :values="values"
                                            />
                                        </div>
                                        <!-- Target Rules (sub-section of Target panel) -->
                                        <div
                                            v-if="
                                                isDraft ||
                                                (contextRules &&
                                                    contextRules.length > 0)
                                            "
                                            class="mt-8 border-t border-taupe-200 pt-8"
                                        >
                                            <div
                                                class="space-y-4 p-1"
                                                data-testid="target-rules-panel"
                                            >
                                                <div>
                                                    <div
                                                        class="flex items-center space-x-3"
                                                    >
                                                        <h3
                                                            class="text-base font-medium text-taupe-900"
                                                        >
                                                            Target Rules
                                                        </h3>
                                                        <zen-button
                                                            v-if="isDraft"
                                                            data-testid="add-target-rule-btn"
                                                            aria-label="Add target rule"
                                                            variant="outline-primary"
                                                            icon-only
                                                            @click="
                                                                toggleCreateRule
                                                            "
                                                        >
                                                            <svg
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                viewBox="0 0 24 24"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                stroke-width="2"
                                                                stroke-linecap="round"
                                                                stroke-linejoin="round"
                                                                class="h-4 w-4"
                                                            >
                                                                <path
                                                                    stroke="none"
                                                                    d="M0 0h24v24H0z"
                                                                    fill="none"
                                                                />
                                                                <path
                                                                    d="M12 5l0 14"
                                                                />
                                                                <path
                                                                    d="M5 12l14 0"
                                                                />
                                                            </svg>
                                                        </zen-button>
                                                    </div>
                                                    <p
                                                        class="mt-1 text-sm text-taupe-500"
                                                    >
                                                        The rules below will be
                                                        evaluated in the order
                                                        below, and if there is a
                                                        match, the value
                                                        associated with the rule
                                                        will be substituted for
                                                        the value set in the
                                                        targeting above.
                                                    </p>
                                                </div>

                                                <div
                                                    v-if="
                                                        contextRules &&
                                                        contextRules.length ==
                                                            0 &&
                                                        !modals.createRule
                                                            .show &&
                                                        !isDraft
                                                    "
                                                    class="rounded-md border border-dashed border-taupe-300 bg-taupe-50"
                                                >
                                                    <zen-empty-table-message
                                                        compact
                                                        icon="ZenIconTarget"
                                                    >
                                                        There are no target
                                                        rules set for this flag
                                                        in this environment.
                                                    </zen-empty-table-message>
                                                </div>

                                                <draggable
                                                    v-model="contextRules"
                                                    item-key="ulid"
                                                    @start="onDragStart"
                                                    @end="onDragEnd"
                                                    :disabled="!isDraft"
                                                    class="space-y-4"
                                                    :class="{
                                                        'dragging-active':
                                                            dragging,
                                                    }"
                                                    handle=".handle"
                                                    ghost-class="dragging-ghost"
                                                    drag-class="dragging-item"
                                                    :animation="200"
                                                >
                                                    <template
                                                        #item="{ element }"
                                                    >
                                                        <div
                                                            class="drag-item-wrapper"
                                                        >
                                                            <context-rule
                                                                :rule="element"
                                                                :flag="result"
                                                                :read-only="
                                                                    isReadOnly
                                                                "
                                                                :draftTarget="
                                                                    draftTarget
                                                                "
                                                                :target="
                                                                    currentTarget
                                                                "
                                                                :falseValue="
                                                                    falseValue
                                                                "
                                                                :trueValue="
                                                                    trueValue
                                                                "
                                                                :values="values"
                                                                :draggable="
                                                                    isDraft
                                                                "
                                                                @removed="
                                                                    removeRule
                                                                "
                                                                @updated="
                                                                    updateRule
                                                                "
                                                            />
                                                        </div>
                                                    </template>
                                                </draggable>

                                                <context-rule
                                                    v-if="
                                                        modals.createRule.show
                                                    "
                                                    :flag="result"
                                                    :rule="
                                                        modals.createRule.data
                                                            .rule
                                                    "
                                                    :draftTarget="draftTarget"
                                                    :target="draftTarget"
                                                    :falseValue="falseValue"
                                                    :trueValue="trueValue"
                                                    :values="values"
                                                    :draggable="isDraft"
                                                    @cancelled="
                                                        toggleCreateRule
                                                    "
                                                    @created="createRule"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <swap-source-target-panel
                                        v-if="swapTargetView"
                                        :flag="result"
                                        :source-name="swapTargetView.sourceName"
                                        :expires-at="swapTargetView.expiresAt"
                                        :target="swapTargetView.target"
                                        :rules="swapTargetView.rules"
                                        :snapshot="swapTargetView.snapshot"
                                        :snapshot-taken-at="
                                            swapTargetView.snapshotTakenAt
                                        "
                                        :not-served="!!swapTargetView.notServed"
                                        :rollout="swapTargetView.rollout"
                                        :values="values"
                                        :true-value="trueValue"
                                        :false-value="falseValue"
                                    />
                                </div>
                            </div>

                            <div
                                v-if="
                                    !swapTargetView &&
                                    (isDraft || (!isDraft && rolloutTarget))
                                "
                                class="space-y-6"
                                data-testid="target-sections-divider"
                            >
                                <!-- Percentage Rollout (draft — configure before publish) -->
                                <div v-if="isDraft && draftTarget">
                                    <percentage-rollout
                                        :project-key="
                                            currentEnvironment.projectKey
                                        "
                                        :environment-key="
                                            currentEnvironment.environmentKey
                                        "
                                        :target-ulid="draftTarget.ulid"
                                        :rollout-data="draftRolloutData"
                                        :is-published="false"
                                        :read-only="false"
                                        @updated="onRolloutUpdated"
                                        @draft-rollout-change="
                                            onDraftRolloutChange
                                        "
                                    />
                                </div>

                                <div
                                    v-if="isDraft"
                                    class="rounded-lg border border-taupe-200 bg-white p-5"
                                    data-testid="schedule-draft-panel"
                                >
                                    <div>
                                        <div
                                            class="flex items-center space-x-3"
                                        >
                                            <h3
                                                class="text-lg font-medium text-taupe-900"
                                            >
                                                Schedule
                                            </h3>
                                            <zen-button
                                                v-if="!draftScheduleEnabled"
                                                data-testid="enable-schedule-checkbox"
                                                aria-label="Enable schedule"
                                                variant="outline-primary"
                                                icon-only
                                                @click="
                                                    draftScheduleEnabled = true
                                                "
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    stroke-width="2"
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                    class="h-4 w-4"
                                                >
                                                    <path
                                                        stroke="none"
                                                        d="M0 0h24v24H0z"
                                                        fill="none"
                                                    />
                                                    <path d="M12 5l0 14" />
                                                    <path d="M5 12l14 0" />
                                                </svg>
                                            </zen-button>
                                            <zen-button
                                                v-else
                                                data-testid="enable-schedule-checkbox"
                                                aria-label="Disable schedule"
                                                variant="outline-danger"
                                                icon-only
                                                @click="
                                                    draftScheduleEnabled = false
                                                "
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    stroke-width="2"
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                    class="h-4 w-4"
                                                >
                                                    <path
                                                        stroke="none"
                                                        d="M0 0h24v24H0z"
                                                        fill="none"
                                                    />
                                                    <path d="M5 12l14 0" />
                                                </svg>
                                            </zen-button>
                                        </div>
                                        <p class="mt-1 text-sm text-taupe-500">
                                            Schedule when this draft target
                                            should be published.
                                        </p>
                                    </div>

                                    <div
                                        v-if="draftScheduleEnabled"
                                        class="mt-5 rounded-lg border border-taupe-200 bg-white p-5"
                                        data-testid="schedule-configure-panel"
                                    >
                                        <div
                                            class="grid grid-cols-1 gap-5 lg:grid-cols-2"
                                        >
                                            <div
                                                data-testid="schedule-date-picker"
                                            >
                                                <UCalendar
                                                    id="draft-scheduled-date"
                                                    v-model="
                                                        draftScheduleCalendarDate
                                                    "
                                                    :min-value="
                                                        scheduleMinCalendarDate()
                                                    "
                                                    :ui="calendarUi"
                                                />
                                            </div>

                                            <div
                                                class="flex items-center justify-center"
                                                data-testid="schedule-time-picker"
                                            >
                                                <ScheduleTimePicker
                                                    id="draft-scheduled-time"
                                                    v-model="draftScheduleTime"
                                                    :min-date="
                                                        scheduleTimeMinDate()
                                                    "
                                                />
                                            </div>
                                        </div>

                                        <div
                                            class="mt-4 text-sm text-taupe-600"
                                        >
                                            Scheduled for:
                                            <zen-date-time
                                                :value="
                                                    modals.selectTarget.data
                                                        .scheduled_at
                                                "
                                                format="short"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <!-- Percentage Rollout (read-only — active rollout on published target) -->
                                <div
                                    v-if="!isDraft && rolloutTarget"
                                    data-testid="rollout-published-section"
                                >
                                    <percentage-rollout
                                        :project-key="
                                            currentEnvironment.projectKey
                                        "
                                        :environment-key="
                                            currentEnvironment.environmentKey
                                        "
                                        :target-ulid="rolloutTarget.ulid"
                                        :rollout-data="rolloutTargetData"
                                        :active-rollout-value="
                                            rolloutTarget.value?.data
                                                ?.display || '(empty)'
                                        "
                                        :active-rollout-rules="
                                            rolloutTargetRules || []
                                        "
                                        :is-published="true"
                                        :read-only="
                                            !['active', 'paused'].includes(
                                                rolloutTargetData?.status
                                            )
                                        "
                                        @updated="onRolloutUpdated"
                                    />
                                </div>
                            </div>

                            <div
                                v-if="isDraft"
                                class="border-t pt-4 pb-4 border-taupe-200 flex items-center space-x-3"
                            >
                                <zen-button
                                    @click="toggleSelectTargetModal"
                                    type="button"
                                    data-testid="publish-draft-btn"
                                    variant="primary"
                                >
                                    Publish
                                </zen-button>

                                <zen-button
                                    @click="toggleDraftTargetDeleteModal"
                                    type="button"
                                    variant="outline-primary"
                                >
                                    Delete Draft
                                </zen-button>

                                <zen-button
                                    @click="closeDraftMode"
                                    type="button"
                                    variant="tertiary"
                                >
                                    Close
                                </zen-button>
                            </div>
                        </div>
                    </div>

                    <!-- Main content - Usage -->
                    <div
                        v-if="tab === 'usage'"
                        class="flex-1 xl:overflow-y-auto"
                    >
                        <div
                            class="pt-4 pb-8 lg:pb-10"
                            data-testid="flag-tab-content"
                        >
                            <div data-testid="flag-usage-chart-card">
                                <zen-grid :flush="true">
                                    <template v-slot:title>
                                        <div>
                                            <h2
                                                class="text-xl font-medium text-taupe-800"
                                            >
                                                Usage Over Time
                                            </h2>
                                            <p
                                                class="mt-1 text-sm font-normal text-taupe-500"
                                            >
                                                Usage trends for this flag in
                                                the selected environment.
                                            </p>
                                        </div>
                                    </template>
                                    <template v-slot:actions>
                                        <div
                                            class="flex flex-wrap items-center gap-3"
                                        >
                                            <label
                                                for="flag-usage-period-select"
                                                class="sr-only"
                                            >
                                                Usage period
                                            </label>
                                            <select
                                                id="flag-usage-period-select"
                                                v-model="usageHistory.period"
                                                @change="refreshUsageHistory"
                                                class="rounded-md border-taupe-300 py-1.5 pl-2 pr-8 text-sm text-taupe-800 focus:border-sky-500 focus:ring-sky-500"
                                                data-testid="flag-usage-period"
                                            >
                                                <option value="24h">
                                                    Last 24 Hours
                                                </option>
                                                <option value="7d">
                                                    Last 7 Days
                                                </option>
                                                <option value="30d">
                                                    Last 30 Days
                                                </option>
                                                <option value="90d">
                                                    Last 90 Days
                                                </option>
                                            </select>
                                            <label
                                                class="inline-flex items-center gap-2 text-sm text-taupe-700"
                                            >
                                                <input
                                                    v-model="
                                                        usageHistory.compare
                                                    "
                                                    @change="
                                                        refreshUsageHistory
                                                    "
                                                    type="checkbox"
                                                    class="h-4 w-4 rounded border-taupe-300 text-sky-600 focus:ring-sky-500"
                                                    data-testid="flag-usage-compare"
                                                />
                                                Compare previous period
                                            </label>
                                        </div>
                                    </template>

                                    <div
                                        v-if="usageHistory.loading"
                                        class="mt-6"
                                    >
                                        <zen-loading-indicator />
                                    </div>

                                    <div
                                        v-else-if="usageHistory.error"
                                        class="mt-6 rounded-md bg-red-50 p-3 text-sm text-red-700"
                                    >
                                        Unable to load usage history.
                                    </div>

                                    <div
                                        v-else
                                        data-testid="flag-usage-chart"
                                        class="mt-6"
                                    >
                                        <div
                                            class="rounded-lg border border-taupe-100 bg-taupe-50 p-3"
                                        >
                                            <div
                                                v-if="
                                                    usageChartData.datasets
                                                        .length > 0
                                                "
                                                class="h-56"
                                            >
                                                <BarChart
                                                    data-testid="flag-usage-bar-chart"
                                                    :data="usageChartData"
                                                    :options="usageChartOptions"
                                                />
                                            </div>
                                            <zen-empty-table-message
                                                v-else
                                                compact
                                                icon="ZenIconChartBar"
                                            >
                                                No usage recorded for this
                                                period.
                                            </zen-empty-table-message>
                                        </div>

                                        <div
                                            class="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3"
                                        >
                                            <div
                                                v-if="
                                                    usageChartData.datasets
                                                        .length > 0
                                                "
                                                data-testid="flag-usage-current"
                                                class="rounded-md border border-sky-100 bg-sky-50 p-3"
                                            >
                                                <div
                                                    class="text-xs uppercase tracking-wide text-sky-700"
                                                >
                                                    Current
                                                </div>
                                                <div
                                                    class="mt-1 text-lg font-medium text-sky-900"
                                                >
                                                    {{
                                                        usageHistoryTotals &&
                                                        usageHistoryTotals.current !=
                                                            null
                                                            ? usageHistoryTotals.current.toLocaleString()
                                                            : 'N/A'
                                                    }}
                                                </div>
                                            </div>
                                            <div
                                                v-if="usageHistory.compare"
                                                class="rounded-md border border-taupe-200 bg-white p-3"
                                            >
                                                <div
                                                    class="text-xs uppercase tracking-wide text-taupe-500"
                                                >
                                                    Previous
                                                </div>
                                                <div
                                                    class="mt-1 text-lg font-medium text-taupe-800"
                                                >
                                                    {{
                                                        usageHistoryTotals &&
                                                        usageHistoryTotals.previous !=
                                                            null
                                                            ? usageHistoryTotals.previous.toLocaleString()
                                                            : 'N/A'
                                                    }}
                                                </div>
                                            </div>
                                            <div
                                                v-if="usageHistory.compare"
                                                class="rounded-md border border-taupe-200 bg-white p-3"
                                            >
                                                <div
                                                    class="text-xs uppercase tracking-wide text-taupe-500"
                                                >
                                                    Change
                                                </div>
                                                <div
                                                    class="mt-1 text-lg font-medium text-taupe-800"
                                                >
                                                    {{ usageChangeSummary }}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </zen-grid>
                            </div>
                        </div>
                    </div>

                    <!-- Main content - Values -->
                    <div
                        v-if="tab === 'values'"
                        class="flex-1 xl:overflow-y-auto"
                    >
                        <zen-loading-indicator v-if="!tabValuesLoaded" />
                        <div v-if="tabValuesLoaded">
                            <div
                                class="pt-4 pb-8 lg:pb-10"
                                data-testid="flag-tab-content"
                            >
                                <!-- Same notice style as the Target panel: the swap is what locks the values. -->
                                <div
                                    v-if="editingLocked"
                                    role="status"
                                    class="mb-6 rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900"
                                    data-testid="swap-values-notice"
                                >
                                    Values are read-only during an environment
                                    swap.
                                </div>
                                <div data-testid="flag-values-grid">
                                    <zen-grid :flush="true">
                                        <template v-slot:title>
                                            <div>
                                                <h2
                                                    class="text-xl font-medium text-taupe-800"
                                                >
                                                    Values
                                                </h2>
                                                <p
                                                    class="mt-1 text-sm font-normal text-taupe-500"
                                                >
                                                    The values defined below can
                                                    be served to each
                                                    environment.
                                                </p>
                                            </div>
                                        </template>
                                        <zen-grid-table>
                                            <zen-grid-header>
                                                <zen-grid-header-row>
                                                    <zen-grid-header-cell
                                                        :hidable="false"
                                                        title="Value"
                                                    />
                                                    <zen-grid-header-cell
                                                        v-if="
                                                            !(
                                                                result.type ==
                                                                    'json' &&
                                                                jsonUiEnabled
                                                            )
                                                        "
                                                        title="JSON"
                                                    />
                                                    <zen-grid-header-cell
                                                        v-if="
                                                            result.type !=
                                                            'boolean'
                                                        "
                                                        alignment="right"
                                                        title=""
                                                    />
                                                </zen-grid-header-row>
                                            </zen-grid-header>
                                            <zen-grid-body>
                                                <ValueTableRow
                                                    v-for="value in values"
                                                    :key="value.ulid"
                                                    :value="value"
                                                    :type="result.type"
                                                    :json-ui-enabled="
                                                        jsonUiEnabled
                                                    "
                                                    :project-ulid="
                                                        project?.ulid
                                                    "
                                                    :environment-ulid="
                                                        environment?.ulid
                                                    "
                                                    :read-only="editingLocked"
                                                    @removed="valueRemoved"
                                                    @updated="valueUpdated"
                                                />
                                                <ValueTableRow
                                                    v-if="showingCreateValue"
                                                    :type="result.type"
                                                    :json-ui-enabled="
                                                        jsonUiEnabled
                                                    "
                                                    :project-ulid="
                                                        project?.ulid
                                                    "
                                                    :environment-ulid="
                                                        environment?.ulid
                                                    "
                                                    @created="valueCreated"
                                                    @cancelled="
                                                        valueCreatedCancelled
                                                    "
                                                />
                                            </zen-grid-body>
                                        </zen-grid-table>
                                        <template v-slot:actions>
                                            <zen-button
                                                v-if="
                                                    result.type != 'boolean' &&
                                                    !showingCreateValue &&
                                                    !editingLocked
                                                "
                                                @click="toggleShowValueCreate"
                                            >
                                                Create
                                            </zen-button>
                                        </template>
                                    </zen-grid>
                                </div>

                                <div
                                    class="mt-6"
                                    data-testid="flag-environment-targets-grid"
                                >
                                    <zen-grid>
                                        <template v-slot:title>
                                            <div>
                                                <h2
                                                    class="text-xl font-medium text-taupe-800"
                                                >
                                                    Environment Targets
                                                </h2>
                                                <p
                                                    class="mt-1 text-sm font-normal text-taupe-500"
                                                >
                                                    The target for each
                                                    environment.
                                                </p>
                                            </div>
                                        </template>
                                        <zen-grid-table>
                                            <zen-grid-header>
                                                <zen-grid-header-row>
                                                    <zen-grid-header-cell
                                                        :hidable="false"
                                                        title="Environment"
                                                    />
                                                    <zen-grid-header-cell
                                                        title="Value"
                                                    />
                                                    <zen-grid-header-cell
                                                        v-if="
                                                            showEnvironmentTargetJsonColumn
                                                        "
                                                        title="JSON"
                                                    />
                                                </zen-grid-header-row>
                                            </zen-grid-header>
                                            <zen-grid-body>
                                                <zen-grid-body-row
                                                    v-for="target in flagTargets"
                                                    :key="target.ulid"
                                                >
                                                    <zen-grid-body-cell
                                                        :hidable="false"
                                                        :primary="true"
                                                    >
                                                        {{
                                                            target.environment
                                                                .data.name
                                                        }}
                                                        <zen-grid-mobile-detail>
                                                            <zen-grid-mobile-detail-item
                                                                label="Value"
                                                            >
                                                                <span
                                                                    v-if="
                                                                        isEnvironmentTargetBlankString(
                                                                            target
                                                                        )
                                                                    "
                                                                    class="italic"
                                                                >
                                                                    (empty)
                                                                </span>
                                                                <span
                                                                    v-else
                                                                    :title="
                                                                        environmentTargetValueTitle(
                                                                            target
                                                                        )
                                                                    "
                                                                >
                                                                    {{
                                                                        environmentTargetValueLabel(
                                                                            target
                                                                        )
                                                                    }}
                                                                </span>
                                                            </zen-grid-mobile-detail-item>
                                                            <zen-grid-mobile-detail-item
                                                                v-if="
                                                                    showEnvironmentTargetJsonColumn
                                                                "
                                                                label="JSON"
                                                            >
                                                                {{
                                                                    environmentTargetJsonLabel(
                                                                        target
                                                                    )
                                                                }}
                                                            </zen-grid-mobile-detail-item>
                                                        </zen-grid-mobile-detail>
                                                    </zen-grid-body-cell>
                                                    <zen-grid-body-cell>
                                                        <span
                                                            v-if="
                                                                isEnvironmentTargetBlankString(
                                                                    target
                                                                )
                                                            "
                                                            class="italic"
                                                        >
                                                            (empty)
                                                        </span>
                                                        <span
                                                            v-else
                                                            :title="
                                                                environmentTargetValueTitle(
                                                                    target
                                                                )
                                                            "
                                                        >
                                                            {{
                                                                environmentTargetValueLabel(
                                                                    target
                                                                )
                                                            }}
                                                        </span>
                                                    </zen-grid-body-cell>
                                                    <zen-grid-body-cell
                                                        v-if="
                                                            showEnvironmentTargetJsonColumn
                                                        "
                                                        :content="
                                                            environmentTargetJsonLabel(
                                                                target
                                                            )
                                                        "
                                                    />
                                                </zen-grid-body-row>
                                            </zen-grid-body>
                                        </zen-grid-table>
                                    </zen-grid>
                                </div>

                                <div class="py-10">
                                    <form
                                        @submit.prevent="confirmValueDefault"
                                        class="divide-taupe-200 space-y-8 divide-y"
                                    >
                                        <div
                                            class="grid grid-cols-1 gap-y-6 sm:grid-cols-6 sm:gap-x-6"
                                        >
                                            <div class="sm:col-span-6">
                                                <h2
                                                    class="text-xl font-medium text-taupe-800"
                                                >
                                                    Default value
                                                </h2>
                                                <p
                                                    class="mt-1 text-sm text-taupe-500"
                                                >
                                                    This is the value that will
                                                    be assigned to a new
                                                    environment when it is
                                                    created. This will not
                                                    affect any existing
                                                    environments. To change an
                                                    existing environment, change
                                                    the environment target.
                                                </p>
                                            </div>
                                            <div
                                                class="sm:col-span-6 flex space-x-3"
                                            >
                                                <div class="py-2">
                                                    <label
                                                        for="value-default"
                                                        class="font-base text-taupe-800"
                                                        >Default:</label
                                                    >
                                                </div>
                                                <select
                                                    name="value-default"
                                                    id="value-default"
                                                    v-model="
                                                        modals.valueDefault.ulid
                                                    "
                                                    :disabled="editingLocked"
                                                    class="w-1/4 rounded-md border-taupe-300 text-taupe-800 shadow-xs focus:border-sky-500 focus:ring-sky-500 disabled:cursor-not-allowed disabled:opacity-60"
                                                >
                                                    <option
                                                        v-for="value in values"
                                                        :key="value.ulid"
                                                        :value="value.ulid"
                                                    >
                                                        {{
                                                            valueOptionLabel(
                                                                value
                                                            )
                                                        }}
                                                    </option>
                                                </select>
                                                <zen-button
                                                    type="submit"
                                                    variant="primary"
                                                    :disabled="editingLocked"
                                                    :processing="
                                                        modals.valueDefault
                                                            .isSaving
                                                    "
                                                >
                                                    Save
                                                </zen-button>
                                            </div>
                                            <div></div>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Main content - History -->
                    <div
                        v-if="tab === 'history'"
                        class="flex-1 xl:overflow-y-auto"
                    >
                        <zen-loading-indicator v-if="!tabHistoryLoaded" />
                        <div v-if="tabHistoryLoaded">
                            <div
                                class="pt-4 pb-10 lg:pb-4"
                                data-testid="flag-tab-content"
                            >
                                <div class="lg:hidden">
                                    <h2
                                        class="text-xl font-medium text-taupe-800"
                                    >
                                        History
                                    </h2>
                                    <p class="mt-1 text-sm text-taupe-500">
                                        A complete timeline of this flag's
                                        published and upcoming changes in this
                                        environment, including scheduled
                                        updates, percentage rollout state, and
                                        target rules.
                                    </p>
                                </div>

                                <div
                                    class="mt-6 lg:mt-0 overflow-x-hidden"
                                    data-testid="flag-history-grid"
                                >
                                    <div class="space-y-3 lg:hidden">
                                        <article
                                            v-for="value in sortedHistory"
                                            :key="value.ulid + '-mobile-card'"
                                            class="rounded-xl border border-taupe-200 bg-white p-4"
                                        >
                                            <div
                                                class="flex items-start justify-between gap-3"
                                            >
                                                <div>
                                                    <p
                                                        class="font-medium text-taupe-900"
                                                    >
                                                        {{
                                                            historyValueLabel(
                                                                value
                                                            )
                                                        }}
                                                    </p>
                                                    <p
                                                        class="mt-1 text-xs text-taupe-600"
                                                    >
                                                        {{
                                                            historyChangeSummary(
                                                                value
                                                            )
                                                        }}
                                                    </p>
                                                </div>
                                                <span
                                                    class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1"
                                                    :class="
                                                        historyStatusClasses(
                                                            value
                                                        )
                                                    "
                                                >
                                                    {{
                                                        historyStatusLabel(
                                                            value
                                                        )
                                                    }}
                                                </span>
                                            </div>
                                            <dl
                                                class="mt-3 grid grid-cols-1 gap-2 text-sm text-taupe-700"
                                            >
                                                <div>
                                                    <dt
                                                        class="text-xs uppercase tracking-wide text-taupe-500"
                                                    >
                                                        Effective
                                                    </dt>
                                                    <dd>
                                                        <zen-date-time
                                                            :value="
                                                                historyTimestamp(
                                                                    value
                                                                )
                                                            "
                                                        />
                                                    </dd>
                                                </div>
                                                <div>
                                                    <dt
                                                        class="text-xs uppercase tracking-wide text-taupe-500"
                                                    >
                                                        Rollout
                                                    </dt>
                                                    <dd>
                                                        {{
                                                            historyRolloutSummary(
                                                                value
                                                            )
                                                        }}
                                                    </dd>
                                                </div>
                                                <div>
                                                    <dt
                                                        class="text-xs uppercase tracking-wide text-taupe-500"
                                                    >
                                                        Rules
                                                    </dt>
                                                    <dd
                                                        :title="
                                                            historyRuleTooltip(
                                                                value
                                                            )
                                                        "
                                                        class="break-words"
                                                    >
                                                        {{
                                                            historyRulePreview(
                                                                value
                                                            )
                                                        }}
                                                    </dd>
                                                </div>
                                                <div>
                                                    <dt
                                                        class="text-xs uppercase tracking-wide text-taupe-500"
                                                    >
                                                        Actor
                                                    </dt>
                                                    <dd>
                                                        {{
                                                            historyActor(value)
                                                        }}
                                                    </dd>
                                                </div>
                                                <div>
                                                    <dt
                                                        class="text-xs uppercase tracking-wide text-taupe-500"
                                                    >
                                                        Comment
                                                    </dt>
                                                    <dd
                                                        class="line-clamp-2 break-words"
                                                    >
                                                        {{
                                                            value.comment ||
                                                            'No comment'
                                                        }}
                                                    </dd>
                                                </div>
                                            </dl>
                                        </article>
                                    </div>

                                    <zen-grid
                                        class="hidden lg:block"
                                        :flush="true"
                                    >
                                        <template v-slot:title>
                                            <div>
                                                <h2
                                                    class="text-xl font-medium text-taupe-800"
                                                >
                                                    History
                                                </h2>
                                                <p
                                                    class="mt-1 text-sm font-normal text-taupe-500"
                                                >
                                                    A complete timeline of this
                                                    flag's published and
                                                    upcoming changes in this
                                                    environment, including
                                                    scheduled updates,
                                                    percentage rollout state,
                                                    and target rules.
                                                </p>
                                            </div>
                                        </template>
                                        <zen-grid-table>
                                            <zen-grid-header>
                                                <zen-grid-header-row>
                                                    <zen-grid-header-cell
                                                        :hidable="false"
                                                        title="Change"
                                                    />
                                                    <zen-grid-header-cell
                                                        title="Effective"
                                                    />
                                                    <zen-grid-header-cell
                                                        title="Details"
                                                    />
                                                    <zen-grid-header-cell
                                                        title="Comment"
                                                    />
                                                </zen-grid-header-row>
                                            </zen-grid-header>
                                            <zen-grid-body>
                                                <template
                                                    v-for="value in sortedHistory"
                                                    :key="value.ulid"
                                                >
                                                    <zen-grid-body-row>
                                                        <zen-grid-body-cell
                                                            :hidable="false"
                                                            :primary="true"
                                                        >
                                                            <div
                                                                class="flex items-center gap-2"
                                                            >
                                                                <button
                                                                    class="cursor-pointer inline-flex h-6 w-6 items-center justify-center rounded-md border border-sky-200 bg-white text-sky-700 hover:bg-sky-50"
                                                                    :aria-label="
                                                                        isHistoryExpanded(
                                                                            value.ulid
                                                                        )
                                                                            ? 'Collapse target details'
                                                                            : 'Expand target details'
                                                                    "
                                                                    @click="
                                                                        toggleHistoryExpanded(
                                                                            value.ulid
                                                                        )
                                                                    "
                                                                >
                                                                    <svg
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                        viewBox="0 0 24 24"
                                                                        fill="none"
                                                                        stroke="currentColor"
                                                                        stroke-width="2"
                                                                        stroke-linecap="round"
                                                                        stroke-linejoin="round"
                                                                        class="h-3.5 w-3.5 transition-transform"
                                                                        :class="{
                                                                            'rotate-45':
                                                                                isHistoryExpanded(
                                                                                    value.ulid
                                                                                ),
                                                                        }"
                                                                    >
                                                                        <path
                                                                            d="M12 5v14"
                                                                        />
                                                                        <path
                                                                            d="M5 12h14"
                                                                        />
                                                                    </svg>
                                                                </button>
                                                                <span
                                                                    class="font-medium text-taupe-900 break-all"
                                                                >
                                                                    {{
                                                                        isHistoryValueExpanded(
                                                                            value.ulid
                                                                        )
                                                                            ? historyValueFullText(
                                                                                  value
                                                                              )
                                                                            : historyValueLabel(
                                                                                  value
                                                                              )
                                                                    }}
                                                                </span>
                                                                <button
                                                                    v-if="
                                                                        isHistoryValueTruncated(
                                                                            value
                                                                        )
                                                                    "
                                                                    type="button"
                                                                    class="cursor-pointer shrink-0 text-xs font-medium text-sky-600 hover:text-sky-700"
                                                                    @click="
                                                                        toggleHistoryValueExpanded(
                                                                            value.ulid
                                                                        )
                                                                    "
                                                                >
                                                                    {{
                                                                        isHistoryValueExpanded(
                                                                            value.ulid
                                                                        )
                                                                            ? 'Show less'
                                                                            : 'Show more'
                                                                    }}
                                                                </button>
                                                            </div>
                                                        </zen-grid-body-cell>
                                                        <zen-grid-body-cell>
                                                            <zen-date-time
                                                                :value="
                                                                    historyTimestamp(
                                                                        value
                                                                    )
                                                                "
                                                            />
                                                        </zen-grid-body-cell>
                                                        <zen-grid-body-cell>
                                                            <div
                                                                class="flex flex-wrap items-center gap-1.5"
                                                            >
                                                                <span
                                                                    class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1"
                                                                    :class="
                                                                        historyStatusClasses(
                                                                            value
                                                                        )
                                                                    "
                                                                >
                                                                    {{
                                                                        historyStatusLabel(
                                                                            value
                                                                        )
                                                                    }}
                                                                </span>
                                                                <span
                                                                    v-if="
                                                                        historyHasRules(
                                                                            value
                                                                        )
                                                                    "
                                                                    class="inline-flex items-center rounded-full bg-sky-50 px-2.5 py-0.5 text-xs font-medium text-sky-700 ring-1 ring-sky-200"
                                                                >
                                                                    Target rules
                                                                </span>
                                                                <span
                                                                    v-if="
                                                                        historyHasRollout(
                                                                            value
                                                                        )
                                                                    "
                                                                    class="inline-flex items-center rounded-full bg-violet-50 px-2.5 py-0.5 text-xs font-medium text-violet-700 ring-1 ring-violet-200"
                                                                >
                                                                    Rollout
                                                                </span>
                                                            </div>
                                                        </zen-grid-body-cell>
                                                        <zen-grid-body-cell>
                                                            <div
                                                                class="max-w-[28rem] whitespace-normal break-words leading-snug"
                                                            >
                                                                {{
                                                                    value.comment ||
                                                                    'No comment'
                                                                }}
                                                            </div>
                                                        </zen-grid-body-cell>
                                                    </zen-grid-body-row>

                                                    <zen-grid-body-row
                                                        v-if="
                                                            isHistoryExpanded(
                                                                value.ulid
                                                            )
                                                        "
                                                        :key="
                                                            value.ulid +
                                                            '-details'
                                                        "
                                                    >
                                                        <td
                                                            colspan="4"
                                                            class="px-4 py-3 text-sm text-taupe-700"
                                                        >
                                                            <div
                                                                class="rounded-lg border border-taupe-200 bg-taupe-50 p-3"
                                                            >
                                                                <div
                                                                    class="grid grid-cols-1 gap-2 text-sm md:grid-cols-2"
                                                                >
                                                                    <div>
                                                                        <span
                                                                            class="font-medium text-taupe-900"
                                                                            >Status:</span
                                                                        >
                                                                        {{
                                                                            historyStatusLabel(
                                                                                value
                                                                            )
                                                                        }}
                                                                    </div>
                                                                    <div>
                                                                        <span
                                                                            class="font-medium text-taupe-900"
                                                                            >Effective:</span
                                                                        >
                                                                        <span
                                                                            class="ml-1 inline-block"
                                                                        >
                                                                            <zen-date-time
                                                                                :value="
                                                                                    historyTimestamp(
                                                                                        value
                                                                                    )
                                                                                "
                                                                            />
                                                                        </span>
                                                                    </div>
                                                                    <div>
                                                                        <span
                                                                            class="font-medium text-taupe-900"
                                                                            >Actor:</span
                                                                        >
                                                                        {{
                                                                            historyActor(
                                                                                value
                                                                            )
                                                                        }}
                                                                    </div>
                                                                    <div>
                                                                        <span
                                                                            class="font-medium text-taupe-900"
                                                                            >Rollout:</span
                                                                        >
                                                                        {{
                                                                            historyRolloutSummary(
                                                                                value
                                                                            )
                                                                        }}
                                                                    </div>
                                                                </div>

                                                                <div
                                                                    class="mt-3"
                                                                >
                                                                    <p
                                                                        class="font-medium text-taupe-900"
                                                                    >
                                                                        Rules in
                                                                        place
                                                                    </p>
                                                                    <p
                                                                        v-if="
                                                                            historyRules(
                                                                                value
                                                                            )
                                                                                .length ===
                                                                            0
                                                                        "
                                                                        class="mt-1 text-taupe-500"
                                                                    >
                                                                        No rules
                                                                        were
                                                                        active
                                                                        for this
                                                                        target.
                                                                    </p>
                                                                    <div
                                                                        v-else
                                                                        class="mt-2 space-y-2"
                                                                    >
                                                                        <div
                                                                            v-for="(
                                                                                rule,
                                                                                ruleIndex
                                                                            ) in historyRules(
                                                                                value
                                                                            )"
                                                                            :key="
                                                                                rule.ulid ||
                                                                                `${value.ulid}-rule-${ruleIndex}`
                                                                            "
                                                                            class="rounded-md border border-taupe-200 bg-white p-2"
                                                                        >
                                                                            <p
                                                                                class="font-medium text-taupe-900"
                                                                            >
                                                                                {{
                                                                                    rule.description ||
                                                                                    'Rule'
                                                                                }}
                                                                            </p>
                                                                            <ul
                                                                                class="mt-1 list-disc space-y-1 pl-5 text-xs text-taupe-700"
                                                                            >
                                                                                <li
                                                                                    v-for="(
                                                                                        criterion,
                                                                                        criterionIndex
                                                                                    ) in historyRuleCriteria(
                                                                                        rule
                                                                                    )"
                                                                                    :key="
                                                                                        criterion.ulid ||
                                                                                        `${value.ulid}-rule-${ruleIndex}-criterion-${criterionIndex}`
                                                                                    "
                                                                                >
                                                                                    {{
                                                                                        historyCriterionDetail(
                                                                                            criterion
                                                                                        )
                                                                                    }}
                                                                                </li>
                                                                            </ul>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                <div
                                                                    v-if="
                                                                        result?.type ===
                                                                        'json'
                                                                    "
                                                                    class="mt-3"
                                                                    data-testid="history-value-diff"
                                                                >
                                                                    <div
                                                                        class="flex items-center justify-between gap-2"
                                                                    >
                                                                        <p
                                                                            class="font-medium text-taupe-900"
                                                                        >
                                                                            Value
                                                                            change
                                                                        </p>
                                                                        <zen-button
                                                                            v-if="
                                                                                canRollbackToTarget(
                                                                                    value
                                                                                ) &&
                                                                                can(
                                                                                    PERMISSIONS.FLAG_UPDATE,
                                                                                    {
                                                                                        projectUlid:
                                                                                            project?.ulid,
                                                                                        environmentUlid:
                                                                                            environment?.ulid,
                                                                                    }
                                                                                )
                                                                            "
                                                                            variant="outline-primary"
                                                                            size="small"
                                                                            data-testid="rollback-target-btn"
                                                                            @click="
                                                                                openRollbackModal(
                                                                                    value
                                                                                )
                                                                            "
                                                                        >
                                                                            Roll
                                                                            back
                                                                            to
                                                                            this
                                                                            version
                                                                        </zen-button>
                                                                    </div>
                                                                    <div
                                                                        class="mt-2"
                                                                    >
                                                                        <zen-json-diff
                                                                            :old-value="
                                                                                historyPreviousDisplay(
                                                                                    value
                                                                                )
                                                                            "
                                                                            :new-value="
                                                                                value
                                                                                    .value
                                                                                    ?.data
                                                                                    ?.display
                                                                            "
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </zen-grid-body-row>
                                                </template>
                                            </zen-grid-body>
                                        </zen-grid-table>
                                    </zen-grid>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Main content - Settings -->
                    <div
                        v-if="tab === 'settings'"
                        class="flex-1 xl:overflow-y-auto"
                    >
                        <div
                            class="pt-4 pb-10 lg:pb-4"
                            data-testid="flag-tab-content"
                        >
                            <form
                                @submit.prevent="submitFlagSettings"
                                class="space-y-4"
                            >
                                <div
                                    v-if="flagUpdatedElsewhere"
                                    data-testid="flag-settings-updated-elsewhere"
                                    role="status"
                                    class="flex flex-col gap-3 rounded-xl border border-sky-200 bg-sky-50 p-4 sm:flex-row sm:items-center sm:justify-between"
                                >
                                    <p class="text-sm text-taupe-800">
                                        This flag was updated elsewhere. Reload
                                        to see the latest settings, which
                                        discards your unsaved changes.
                                    </p>
                                    <zen-button
                                        type="button"
                                        variant="secondary"
                                        @click="reloadUpdatedFlag"
                                    >
                                        Reload
                                    </zen-button>
                                </div>
                                <div
                                    data-testid="flag-settings-general-section"
                                    class="grid grid-cols-1 gap-y-6 rounded-xl border border-taupe-200 bg-white p-6 sm:grid-cols-6 sm:gap-x-6 sm:p-7"
                                >
                                    <div class="sm:col-span-6">
                                        <h2
                                            class="text-lg font-medium text-taupe-900"
                                        >
                                            General
                                        </h2>
                                        <p class="mt-1 text-sm text-taupe-500">
                                            This information will be displayed
                                            as part of the flag and in the
                                            public API response.
                                        </p>
                                    </div>
                                    <div class="sm:col-span-6">
                                        <label
                                            for="name"
                                            class="block text-sm font-medium text-taupe-800"
                                            >Name</label
                                        >
                                        <input
                                            type="text"
                                            name="name"
                                            id="name"
                                            v-model="
                                                modals.updateResult.data.name
                                            "
                                            class="mt-2 block w-full max-w-2xl rounded-md border-taupe-300 bg-white px-3 py-2 text-taupe-900 shadow-xs focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
                                        />
                                    </div>
                                    <div class="sm:col-span-6">
                                        <label
                                            for="key"
                                            class="block text-sm font-medium text-taupe-800"
                                            >Key</label
                                        >
                                        <div
                                            class="mt-2 inline-flex rounded-md border border-taupe-200 bg-taupe-50 px-3 py-1.5 font-mono text-sm text-taupe-700"
                                        >
                                            {{ result.key }}
                                        </div>
                                    </div>
                                    <div class="sm:col-span-6">
                                        <label
                                            for="type"
                                            class="block text-sm font-medium text-taupe-800"
                                            >Type</label
                                        >
                                        <div
                                            class="mt-2 inline-flex rounded-md border border-taupe-200 bg-taupe-50 px-3 py-1.5 font-mono text-sm text-taupe-700"
                                        >
                                            {{ result.type }}
                                        </div>
                                    </div>
                                    <div class="sm:col-span-6">
                                        <label
                                            for="description"
                                            class="block text-sm font-medium text-taupe-800"
                                            >Description</label
                                        >
                                        <div class="mt-1">
                                            <textarea
                                                id="description"
                                                name="description"
                                                v-model="
                                                    modals.updateResult.data
                                                        .description
                                                "
                                                rows="4"
                                                class="block w-full rounded-md border-taupe-300 bg-white px-3 py-2 text-taupe-900 shadow-xs focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
                                            ></textarea>
                                        </div>
                                        <p class="mt-3 text-sm text-taupe-500">
                                            Brief description of the
                                            functionality that the flag controls
                                            in your application.
                                        </p>
                                    </div>
                                    <div class="sm:col-span-6">
                                        <label
                                            for="permanent"
                                            class="block text-sm font-medium text-taupe-800"
                                            >Permanent</label
                                        >
                                        <div
                                            class="mt-2 rounded-lg border border-sky-100 bg-sky-50 px-4 py-3"
                                        >
                                            <label
                                                for="permanent"
                                                class="flex items-start gap-3"
                                            >
                                                <input
                                                    type="checkbox"
                                                    name="permanent"
                                                    id="permanent"
                                                    data-testid="flag-settings-permanent-checkbox"
                                                    v-model="
                                                        modals.updateResult.data
                                                            .permanent
                                                    "
                                                    class="mt-0.5 h-4 w-4 rounded border-taupe-300"
                                                />
                                                <span
                                                    class="text-sm text-taupe-800"
                                                    >This flag is permanent. Do
                                                    not show in the unused flag
                                                    report.</span
                                                >
                                            </label>
                                        </div>
                                    </div>
                                    <div
                                        data-testid="flag-settings-platform-inclusion-section"
                                        class="sm:col-span-6"
                                    >
                                        <label
                                            class="block text-sm font-medium text-taupe-800"
                                            >Platform Inclusion</label
                                        >
                                        <div
                                            class="mt-2 space-y-3 rounded-lg border border-sky-100 bg-sky-50 px-4 py-3"
                                        >
                                            <label
                                                for="include_in_mobile"
                                                class="flex items-start gap-3"
                                            >
                                                <input
                                                    type="checkbox"
                                                    name="include_in_mobile"
                                                    id="include_in_mobile"
                                                    data-testid="flag-settings-mobile-inclusion-checkbox"
                                                    v-model="
                                                        modals.updateResult.data
                                                            .include_in_mobile
                                                    "
                                                    class="mt-0.5 h-4 w-4 rounded border-taupe-300"
                                                />
                                                <span
                                                    class="text-sm text-taupe-800"
                                                    >Include this flag in mobile
                                                    payloads.</span
                                                >
                                            </label>
                                            <label
                                                for="include_in_client"
                                                class="flex items-start gap-3"
                                            >
                                                <input
                                                    type="checkbox"
                                                    name="include_in_client"
                                                    id="include_in_client"
                                                    data-testid="flag-settings-client-inclusion-checkbox"
                                                    v-model="
                                                        modals.updateResult.data
                                                            .include_in_client
                                                    "
                                                    class="mt-0.5 h-4 w-4 rounded border-taupe-300"
                                                />
                                                <span
                                                    class="text-sm text-taupe-800"
                                                    >Include this flag in client
                                                    payloads.</span
                                                >
                                            </label>
                                        </div>
                                    </div>
                                </div>
                                <div
                                    v-if="
                                        can(PERMISSIONS.FLAG_UPDATE, {
                                            projectUlid: project?.ulid,
                                            environmentUlid: environment?.ulid,
                                        })
                                    "
                                    data-testid="flag-settings-actions"
                                    class="flex justify-end gap-3 border-t border-taupe-200 py-4"
                                >
                                    <zen-button
                                        type="button"
                                        variant="secondary"
                                        :disabled="modals.updateResult.isSaving"
                                        @click="resetUpdateModal"
                                    >
                                        Cancel
                                    </zen-button>
                                    <zen-button
                                        type="submit"
                                        variant="primary"
                                        :processing="
                                            modals.updateResult.isSaving
                                        "
                                    >
                                        Save
                                    </zen-button>
                                </div>
                            </form>
                            <div
                                data-testid="flag-settings-danger-zone"
                                class="w-full space-y-5 border-t border-taupe-200 pt-4"
                            >
                                <div class="space-y-1">
                                    <h2
                                        class="text-2xl font-semibold text-red-600"
                                    >
                                        Danger Zone
                                    </h2>
                                    <p class="text-sm text-taupe-600">
                                        Irreversible and destructive actions.
                                    </p>
                                </div>

                                <div
                                    v-if="
                                        can(PERMISSIONS.FLAG_DELETE, {
                                            projectUlid: project?.ulid,
                                            environmentUlid: environment?.ulid,
                                        })
                                    "
                                    data-testid="flag-danger-zone-card"
                                    class="rounded-xl border-2 border-red-200 bg-red-50 p-6 max-w-md hover:shadow-lg transition-shadow"
                                >
                                    <div class="flex gap-4">
                                        <svg
                                            class="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                stroke-width="2"
                                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                            />
                                        </svg>
                                        <div class="flex-1">
                                            <div
                                                class="font-medium text-red-900 mb-1"
                                            >
                                                Remove this flag
                                            </div>
                                            <p
                                                class="text-sm text-red-800 mb-4"
                                            >
                                                Permanently remove this flag and
                                                all associated targets. This
                                                action cannot be undone.
                                            </p>
                                            <zen-button
                                                variant="primary-danger"
                                                @click="
                                                    openRemoveModal(result.key)
                                                "
                                            >
                                                <template #icon>
                                                    <svg
                                                        class="h-4 w-4"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            stroke-linecap="round"
                                                            stroke-linejoin="round"
                                                            stroke-width="2"
                                                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                                        />
                                                    </svg>
                                                </template>
                                                Remove flag
                                            </zen-button>
                                        </div>
                                    </div>
                                </div>
                                <p class="text-sm text-taupe-500">
                                    This flag was created on
                                    <zen-date-time :value="result.created_at" />
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <remove-flag-modal
                :is-open="modals.removeResult.show"
                @close="toggleRemoveModal"
                @confirm="confirmRemove"
            />

            <delete-draft-target-modal
                :is-open="modals.draftTargetDelete.show"
                :processing="modals.draftTargetDelete.processing"
                @close="toggleDraftTargetDeleteModal"
                @confirm="confirmDraftTargetDelete"
            />

            <cancel-scheduled-target-modal
                :is-open="modals.scheduledTargetDelete.show"
                @close="toggleScheduledTargetDeleteModal"
                @confirm="confirmScheduledTargetDelete"
            />

            <review-publish-modal
                :is-open="modals.selectTarget.show"
                :error="modals.selectTarget.error"
                :flag-name="result?.name"
                :flag-key="result?.key"
                :current-value-display="currentValue?.display"
                :selected-value-display="modals.selectTarget.selected?.display"
                :context-rules="contextRules"
                :draft-rollout-data="draftRolloutData"
                :draft-schedule-enabled="draftScheduleEnabled"
                :scheduled-at="modals.selectTarget.data.scheduled_at"
                :comments-required="environment?.comments_required"
                :confirmation-required="environment?.confirmation_required"
                :allow-target-update="allowTargetUpdate"
                :processing="modals.selectTarget.processing"
                v-model:comment="modals.selectTarget.data.comment"
                v-model:confirmation="modals.selectTarget.confirmation"
                @close="toggleSelectTargetModal"
                @confirm="confirmSelectTarget"
            />

            <view-scheduled-change-modal
                :is-open="modals.viewScheduledChange.show"
                :flag-name="result?.name"
                :flag-key="result?.key"
                :current-value-display="currentValue?.display"
                :new-value-display="scheduledTarget?.value?.data?.display"
                :scheduled-at="scheduledTarget?.scheduled_at"
                @close="toggleViewScheduleChangeModal"
            />

            <rollback-target-modal
                :is-open="modals.rollbackTarget.show"
                :version-timestamp="
                    modals.rollbackTarget.target
                        ? historyTimestamp(modals.rollbackTarget.target)
                        : null
                "
                :flag-key="result?.key"
                :comments-required="environment?.comments_required"
                :confirmation-required="environment?.confirmation_required"
                :allow-rollback="allowRollback"
                :processing="modals.rollbackTarget.processing"
                :error="modals.rollbackTarget.error"
                v-model:comment="modals.rollbackTarget.comment"
                v-model:confirmation="modals.rollbackTarget.confirmation"
                @close="closeRollbackModal"
                @confirm="confirmRollback"
            />
        </section>
    </div>
</template>

<script>
/* External */
import { CalendarDate, getLocalTimeZone } from '@internationalized/date'
import draggable from 'vuedraggable'
import moment from 'moment-timezone'
import { Bar as BarChart } from 'vue-chartjs'
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    BarController,
    LineElement,
    PointElement,
    LineController,
    Tooltip,
    Legend,
} from 'chart.js'

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    BarController,
    LineElement,
    PointElement,
    LineController,
    Tooltip,
    Legend
)

const PREVIOUS_PERIOD_COLOR = 'rgba(107, 114, 128, 0.9)'
const TOTAL_LINE_COLOR = '#111827'

/* Services */
import FlagService from '@/services/flag.service'
import FlagSwapService from '@/services/flag-swap.service'
import FlagTargetService from '@/services/flag-target.service'
import FlagValueService from '@/services/flag-value.service'
import TargetService from '@/services/target.service'
import TargetRuleService from '@/services/target-rule.service'
import TargetValueService from '@/services/target-value.service'
import UsageService from '@/services/usage.service'
import UserService from '@/services/user.service'
import { isOwnRequestId } from '@/utils/api-client'
import { removeBy, upsertBy } from '@/utils/list-mutations'
import { useAccountEventsStore } from '@/stores/account-events'
import { useSessionStore } from '@/stores/session'

import { useDateFormatter } from '@/screens/_system/DateTime.vue'

/* UI: Project Components */
import BooleanTargetToggle from '@/screens/_components/project/BooleanTargetToggle.vue'
import ContextRule from '@/screens/_components/project/ContextRule.vue'
import NumberTargetToggle from '@/screens/_components/project/NumberTargetToggle.vue'
import FlagHeaderTabs from '@/screens/_components/project/FlagHeaderTabs.vue'
import FlagStatsPanel from '@/screens/_components/project/FlagStatsPanel.vue'
import PercentageRollout from '@/screens/_components/project/PercentageRollout.vue'
import StringTargetToggle from '@/screens/_components/project/StringTargetToggle.vue'
import JsonTargetToggle from '@/screens/_components/project/JsonTargetToggle.vue'
import SwapSourceTargetPanel from '@/screens/_components/project/SwapSourceTargetPanel.vue'

import {
    errorMessage,
    infoMessage,
    successMessage,
} from '@/utils/notifications'
import { usePermissions, PERMISSIONS } from '@/composables/usePermissions'
import { isFeatureEnabled } from '@/utils/feature-flags'
import { truncateDisplay, JSON_DISPLAY_MAX_LENGTH } from '@/utils/formatters'

import ValueTableRow from './_ValueTableRow.vue'
import ScheduleTimePicker from './_ScheduleTimePicker.vue'
import RemoveFlagModal from './modals/RemoveFlagModal.vue'
import DeleteDraftTargetModal from './modals/DeleteDraftTargetModal.vue'
import CancelScheduledTargetModal from './modals/CancelScheduledTargetModal.vue'
import ReviewPublishModal from './modals/ReviewPublishModal.vue'
import ViewScheduledChangeModal from './modals/ViewScheduledChangeModal.vue'
import RollbackTargetModal from './modals/RollbackTargetModal.vue'

const FLAG_VALUE_EVENTS = [
    'flag-value-stored',
    'flag-value-updated',
    'flag-value-destroyed',
]

const TARGET_EVENTS = [
    'target-stored',
    'target-updated',
    'target-destroyed',
    'target-rollout-updated',
]

const TARGET_RULE_EVENTS = [
    'target-rule-stored',
    'target-rule-updated',
    'target-rule-destroyed',
    'target-rule-reordered',
]

// A publish can send several target events at once. Refreshes wait this long
// after the last event for the same key so a burst becomes one request.
const REALTIME_REFRESH_DELAY_MS = 250

// setTimeout overflows past about 24.8 days, so a longer swap is re-checked early and scheduled again.
const MAX_TIMER_DELAY_MS = 2147483647

/**
 * The live target from a flag's targets: the first published target without an
 * active rollout, falling back to the first published target.
 *
 * @param {Array<object>} targets
 * @returns {object|undefined}
 */
const pickLiveTarget = (targets) => {
    const published = targets.filter((ev) => ev.status === 'published')

    return (
        published.find(
            (t) =>
                !t.rollout_status ||
                !['active', 'paused'].includes(t.rollout_status)
        ) || published[0]
    )
}

export default {
    name: 'ProjectFlagDetailScreen',
    setup() {
        const { can } = usePermissions()
        const { resolveTimezone, formatDateTime } = useDateFormatter()
        return { can, PERMISSIONS, resolveTimezone, formatDateTime }
    },
    components: {
        ScheduleTimePicker,
        FlagHeaderTabs,
        FlagStatsPanel,
        BooleanTargetToggle,
        NumberTargetToggle,
        StringTargetToggle,
        JsonTargetToggle,
        ContextRule,
        PercentageRollout,
        SwapSourceTargetPanel,
        draggable,
        BarChart,

        ValueTableRow,
        RemoveFlagModal,
        DeleteDraftTargetModal,
        CancelScheduledTargetModal,
        ReviewPublishModal,
        ViewScheduledChangeModal,
        RollbackTargetModal,
    },
    computed: {
        sessionStore() {
            return useSessionStore()
        },
        jsonUiEnabled() {
            return this.jsonUiEnabledValue
        },
        // The Environment Targets "JSON" column shows each target's raw
        // value payload. For a json flag that's the same document the
        // Value column already shows (double-wrapped as {"json":...}), so
        // collapse it the same way ZEN-718 collapsed the Values tab.
        showEnvironmentTargetJsonColumn() {
            return !(this.result?.type === 'json' && this.jsonUiEnabled)
        },
        allowTargetUpdate() {
            return (
                !this.environment?.confirmation_required ||
                this.modals.selectTarget.confirmation == this.result.key
            )
        },
        allowRollback() {
            return (
                !this.environment?.confirmation_required ||
                this.modals.rollbackTarget.confirmation == this.result?.key
            )
        },
        draftScheduleEnabled: {
            get() {
                return this.modals.selectTarget.data.scheduled_at !== null
            },
            set(enabled) {
                this.setScheduledAt(
                    enabled
                        ? this.modals.selectTarget.data.scheduled_at ||
                              new Date()
                        : null
                )
            },
        },
        draftScheduleDate: {
            get() {
                const value = this.modals.selectTarget.data.scheduled_at
                return value ? new Date(value) : null
            },
            set(selectedDate) {
                if (!selectedDate) {
                    this.setScheduledAt(null)
                    return
                }

                const base = this.modals.selectTarget.data.scheduled_at
                    ? new Date(this.modals.selectTarget.data.scheduled_at)
                    : new Date()

                base.setFullYear(
                    selectedDate.getFullYear(),
                    selectedDate.getMonth(),
                    selectedDate.getDate()
                )

                this.setScheduledAt(this.clampScheduledAtToNow(base))
            },
        },
        // UCalendar's v-model works in `@internationalized/date` CalendarDate
        // values rather than plain JS Dates — this bridges it to the same
        // Date-based `draftScheduleDate` computed above.
        draftScheduleCalendarDate: {
            get() {
                const value = this.draftScheduleDate
                if (!value) return null

                return new CalendarDate(
                    value.getFullYear(),
                    value.getMonth() + 1,
                    value.getDate()
                )
            },
            set(calendarDate) {
                this.draftScheduleDate = calendarDate
                    ? calendarDate.toDate(getLocalTimeZone())
                    : null
            },
        },
        draftScheduleTime: {
            get() {
                const value = this.modals.selectTarget.data.scheduled_at
                return value ? new Date(value) : null
            },
            set(selectedTime) {
                if (!selectedTime) {
                    this.setScheduledAt(null)
                    return
                }

                const base = this.modals.selectTarget.data.scheduled_at
                    ? new Date(this.modals.selectTarget.data.scheduled_at)
                    : new Date()

                base.setHours(
                    selectedTime.getHours(),
                    selectedTime.getMinutes(),
                    0,
                    0
                )

                this.setScheduledAt(this.clampScheduledAtToNow(base))
            },
        },
        /**
         * Extract rollout data from the draft target's flat rollout_* fields.
         */
        draftRolloutData() {
            if (!this.isDraft) return null

            const stagedRollout = this.modals.selectTarget?.data?.rollout
            if (stagedRollout !== undefined) {
                return stagedRollout
            }

            return this.extractRolloutData(this.draftTarget)
        },
        /**
         * Whether the current draft target has a rollout configured.
         */
        draftHasRollout() {
            if (!this.isDraft) return false
            const data = this.draftRolloutData
            return data !== null && data.mode !== null
        },
        /**
         * When in read-only mode with an active rollout, find the rollout target.
         * The rollout target is the second published target (the one with rollout data).
         */
        rolloutTarget() {
            if (this.isDraft || !this.targetValues) return null
            const publishedTargets = this.targetValues.filter(
                (t) => t.status === 'published'
            )
            if (publishedTargets.length < 2) return null
            // The rollout target is the one with active rollout data
            return (
                publishedTargets.find(
                    (t) =>
                        t.rollout_status &&
                        ['active', 'paused'].includes(t.rollout_status)
                ) || null
            )
        },
        /**
         * Extract rollout data from the rollout target for the PercentageRollout component.
         */
        rolloutTargetData() {
            return this.extractRolloutData(this.rolloutTarget)
        },
        currentEnvironment() {
            if (this.sessionStore.data === null) {
                return {
                    projectKey: null,
                    environmentKey: null,
                    flags: [],
                }
            }
            return this.sessionStore.data.current
        },
        project() {
            if (!this.sessionStore.data?.projects?.data?.length) {
                return null
            }

            if (!this.currentEnvironment?.projectKey) {
                return null
            }

            return (
                this.sessionStore.data.projects.data.find(
                    (x) => x.key === this.currentEnvironment.projectKey
                ) || null
            )
        },
        environment() {
            if (!this.project?.environments?.data) {
                return null
            }

            if (!this.currentEnvironment?.environmentKey) {
                return null
            }

            const environment = this.project.environments.data.find(
                (x) => x.key === this.currentEnvironment.environmentKey
            )

            return environment || null
        },
        hasResult() {
            return this.result !== null
        },
        hasScheduledTarget() {
            return this.scheduledTarget !== null
        },
        isDraft() {
            return this.draftTarget !== null
        },
        isReadOnly() {
            return !this.isDraft
        },
        selectedKey() {
            return this.$route.params.key
        },
        sortedHistory() {
            if (this.targetValues == null) {
                return []
            }

            return [...this.targetValues]
                .filter((target) => target.status !== 'draft')
                .sort((a, b) => {
                    const timestampDiff =
                        new Date(this.historyTimestamp(b)) -
                        new Date(this.historyTimestamp(a))

                    if (timestampDiff !== 0) {
                        return timestampDiff
                    }

                    const statusPriority = {
                        published: 0,
                        expired: 1,
                        scheduled: 2,
                    }

                    const aPriority = statusPriority[a.status] ?? 99
                    const bPriority = statusPriority[b.status] ?? 99

                    if (aPriority !== bPriority) {
                        return aPriority - bPriority
                    }

                    return a.ulid.localeCompare(b.ulid)
                })
        },
        tabHistoryLoaded() {
            return !this.loading.target && !this.loading.historyRules
        },
        tabTargetLoaded() {
            return (
                !this.loading.contextRule &&
                !this.loading.target &&
                !this.loading.values
            )
        },
        /**
         * Only the first load shows the spinner. Later refreshes update the tab
         * in place, so it never blanks while they run.
         */
        targetTabShown() {
            return this.targetTabReady || this.tabTargetLoaded
        },
        /**
         * Whether a running swap locks this flag's targets and values. A whole-environment swap
         * locks every flag on the environment, including one the snapshot leaves out, because
         * the swap applies to the environment and not to one flag at a time (ZEN-2043). An older
         * swap that only overrides values locks just the flags it overrides.
         */
        editingLocked() {
            return !!this.swap || !!this.environmentSwap
        },
        /**
         * What the Target tab shows in place of the editable target while editing is locked, or
         * null when it isn't. A flag the swap serves shows the swap's source. A flag the snapshot
         * leaves out is still served by this environment's own targeting, so it shows that,
         * read-only, rather than the editable card.
         */
        swapTargetView() {
            if (this.swap) return this.swap
            if (!this.environmentSwap) return null

            const rolloutTarget = this.rolloutTarget

            return {
                ...this.environmentSwap,
                notServed: true,
                target: this.currentTarget,
                rules: this.contextRules ?? [],
                rollout: rolloutTarget
                    ? {
                          target: rolloutTarget,
                          rules: this.rolloutTargetRules ?? [],
                          percentage: rolloutTarget.rollout_percentage,
                          status: rolloutTarget.rollout_status,
                      }
                    : null,
            }
        },
        tabValuesLoaded() {
            return !this.loading.flagTargets && !this.loading.values
        },
        usageHistoryPoints() {
            return this.usageHistory.data?.points || []
        },
        usageHistoryPointsWithSegments() {
            return this.usageHistoryPoints.map((point) => ({
                ...point,
                segments: this.usageSegmentsForPoint(point),
            }))
        },
        usageChartSeriesKeys() {
            // Prefer the API's stable value_series metadata so a value with
            // zero usage in the selected period still keeps its dataset/
            // legend entry instead of disappearing. Only fall back to
            // scanning segments when the API didn't return series metadata
            // at all (see usageSegmentsForPoint's own fallback).
            if (this.usageHistorySeries.length > 0) {
                return this.usageHistorySeries.map((series) => ({
                    key: series.key,
                    label: series.label,
                    color: series.color,
                }))
            }

            const keys = new Map()

            this.usageHistoryPointsWithSegments.forEach((point) => {
                point.segments.forEach((segment) => {
                    if (!keys.has(segment.key)) {
                        keys.set(segment.key, {
                            key: segment.key,
                            label: segment.label,
                            color: segment.color,
                        })
                    }
                })
            })

            return Array.from(keys.values())
        },
        usageChartData() {
            const labels = this.usageHistoryPoints.map((point) =>
                this.usageAxisLabel(point.bucket_start)
            )

            const datasets = this.usageChartSeriesKeys.map((series) => ({
                type: 'line',
                label: series.label,
                borderColor: series.color,
                backgroundColor: series.color,
                pointBackgroundColor: series.color,
                borderWidth: 2,
                pointRadius: 2,
                tension: 0,
                fill: false,
                order: 0,
                data: this.usageHistoryPointsWithSegments.map((point) => {
                    const segment = point.segments.find(
                        (candidate) => candidate.key === series.key
                    )
                    return segment ? segment.count : 0
                }),
            }))

            // With only one value series, that series' own line already is
            // the total, so an overlaid "Total" line would just duplicate it.
            if (this.usageChartSeriesKeys.length > 1) {
                datasets.push({
                    type: 'line',
                    label: 'Total',
                    borderColor: TOTAL_LINE_COLOR,
                    backgroundColor: TOTAL_LINE_COLOR,
                    pointBackgroundColor: TOTAL_LINE_COLOR,
                    borderWidth: 2,
                    borderDash: [6, 4],
                    pointRadius: 2,
                    tension: 0,
                    fill: false,
                    order: 0,
                    data: this.usageHistoryPoints.map((point) =>
                        Number(point?.count || 0)
                    ),
                })
            }

            if (this.usageHistory.compare) {
                datasets.push({
                    type: 'bar',
                    label: 'Previous',
                    backgroundColor: PREVIOUS_PERIOD_COLOR,
                    order: 1,
                    data: this.usageHistoryPoints.map(
                        (point) => point.previous_count ?? 0
                    ),
                })
            }

            return { labels, datasets }
        },
        usageChartOptions() {
            return {
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    x: {},
                    y: { beginAtZero: true },
                },
                plugins: {
                    tooltip: {
                        callbacks: {
                            title: (items) => {
                                const point =
                                    this.usageHistoryPoints[items[0]?.dataIndex]
                                return point
                                    ? this.usageTooltipLabel(point)
                                    : ''
                            },
                        },
                    },
                },
            }
        },
        usageHistoryTotals() {
            return (
                this.usageHistory.data?.totals || {
                    current: 0,
                    previous: null,
                    delta: null,
                    change_percent: null,
                }
            )
        },
        usageHistorySeries() {
            return this.usageHistory.data?.value_series || []
        },
        usageChangeSummary() {
            const delta = this.usageHistoryTotals.delta
            const percent = this.usageHistoryTotals.change_percent

            if (delta === null || percent === null) {
                return 'N/A'
            }

            const sign = delta >= 0 ? '+' : ''
            return `${sign}${delta} (${sign}${percent}%)`
        },
    },
    watch: {
        '$route.hash'(hash) {
            const normalizedHash = (hash || '').replace('#', '')
            const validTabs = [
                'target',
                'usage',
                'values',
                'history',
                'settings',
            ]
            const tab =
                normalizedHash === 'edit-target'
                    ? 'target'
                    : validTabs.includes(normalizedHash)
                      ? normalizedHash
                      : 'target'

            if (this.tab !== tab) {
                this.toggleTab(tab)
            }
        },
        currentEnvironment: {
            handler(environment) {
                if (
                    !environment?.projectKey ||
                    !environment?.environmentKey ||
                    !this.selectedKey ||
                    this.result !== null ||
                    this.loading.flag
                ) {
                    return
                }

                this.loadFlag()
            },
            deep: true,
        },
    },
    data() {
        return {
            calendarUi: {
                root: 'inline-flex w-full',
                header: 'flex items-center justify-between pb-2 mb-2 border-b border-taupe-200',
                heading:
                    'flex items-center gap-1 text-sm font-medium text-taupe-900',
                headingLabel: 'text-taupe-900',
                body: 'flex w-full',
                grid: 'w-full border-collapse text-sm mt-1',
                gridWeekDaysRow: '',
                headCell:
                    'py-2 text-center text-taupe-500 font-medium text-xs uppercase',
                gridBody: '',
                gridRow: '',
                cell: 'p-0.5 text-center',
                cellTrigger: [
                    'flex items-center justify-center w-9 h-9 mx-auto rounded-full text-sm transition-colors cursor-pointer',
                    'text-taupe-900 not-data-selected:not-data-disabled:hover:bg-taupe-100',
                    'data-selected:bg-sky-600 data-selected:text-white hover:data-selected:bg-sky-700',
                    'data-today:not-data-selected:ring-1 data-today:not-data-selected:ring-sky-600 data-today:not-data-selected:font-medium',
                    'data-disabled:text-taupe-300 data-disabled:cursor-default data-disabled:pointer-events-none',
                    'data-outside-view:text-taupe-300',
                ].join(' '),
            },
            lastUse: {
                loading: true,
                value: null,
            },
            usageHistory: {
                loading: false,
                error: null,
                period: '7d',
                compare: false,
                data: {
                    points: [],
                    value_series: [],
                    totals: {
                        current: 0,
                        previous: null,
                        delta: null,
                        change_percent: null,
                    },
                },
            },
            loading: {
                createDraft: false,
                contextRule: true,
                flag: true,
                flagTargets: true,
                hasDraftTargetUlid: true,
                historyRules: false,
                scheduledTarge: false,
                target: true,
                values: true,
            },
            result: null,
            error: null,
            modals: {
                createRule: {
                    show: false,
                    data: {
                        rule: null,
                    },
                    error: {},
                },
                removeResult: {
                    show: false,
                    key: null,
                    error: null,
                },
                updateResult: {
                    data: {},
                    error: null,
                    isSaving: false,
                },
                valueDefault: {
                    data: {},
                    error: null,
                    isSaving: false,
                },
                selectTarget: {
                    confirmation: null,
                    show: false,
                    processing: false,
                    showSchedule: false,
                    selected: null,
                    error: null,
                    data: {
                        comment: null,
                        scheduled_at: null,
                        rollout: null,
                    },
                },
                scheduledTargetDelete: {
                    show: false,
                    error: null,
                },
                draftTargetDelete: {
                    show: false,
                    error: null,
                    processing: false,
                },
                viewScheduledChange: {
                    show: false,
                },
                rollbackTarget: {
                    show: false,
                    target: null,
                    comment: null,
                    confirmation: null,
                    error: null,
                    processing: false,
                },
            },
            tab: (() => {
                const hash = globalThis.window.location.hash.replace('#', '')
                const validTabs = [
                    'target',
                    'usage',
                    'values',
                    'history',
                    'settings',
                ]
                if (hash === 'edit-target') return 'target'
                if (validTabs.includes(hash)) return hash
                return 'target'
            })(),
            tabLoaded: {
                target: false,
                values: false,
                history: false,
            },
            showingCreateValue: false,
            jsonUiEnabledValue: false,
            autoLoadDraft: false,
            draftTarget: null,
            scheduledTarget: null,
            hasDraftTargetUlid: false,
            currentTarget: null,
            currentValue: null,
            contextRules: [],
            rolloutTargetRules: [],
            historyRulesByTarget: {},
            historyExpanded: {},
            historyValueExpanded: {},
            targetValues: [],
            targetTabReady: false,
            // The temporary swap replacing this flag on the environment, with its source's live target and rules (ZEN-2026).
            swap: null,
            // The whole-environment swap running on this environment, whether or not it serves this flag (ZEN-2043).
            environmentSwap: null,
            swapExpiryTimer: null,
            swapRequest: 0,
            flagTargets: [],
            values: [],
            falseValue: null,
            trueValue: null,
            dragging: false,
            accountEventsUnsubscribers: [],
            // Pending debounced refreshes, keyed by what they refresh.
            realtimeRefreshTimers: {},
            // Set when the flag changes elsewhere while the settings form has
            // unsaved edits, so the form is not overwritten under the user.
            flagUpdatedElsewhere: false,
            // What changed to the open draft in another tab or through the API,
            // as { kind: 'modified' | 'left', text, unsaved }. 'left' means the
            // draft was published, scheduled, or deleted and this tab let go of it.
            draftChange: null,
            // The live target this draft was opened against, and whether a
            // different target has since been published for the same flag.
            draftBaseLiveUlid: null,
            draftBaseStale: false,
            // Set while this screen deletes the flag itself, so the deletion
            // event does not show a second notice on top of the redirect.
            removingFlag: false,
        }
    },
    methods: {
        async loadJsonUiFeatureFlag() {
            this.jsonUiEnabledValue = await isFeatureEnabled(
                'allow-json-flags',
                false
            )
        },
        normalizeFlagSettingsData(data) {
            const normalized = JSON.parse(JSON.stringify(data || {}))

            const hasMobileField =
                Object.prototype.hasOwnProperty.call(
                    normalized,
                    'include_in_mobile'
                ) === true
            const hasClientField =
                Object.prototype.hasOwnProperty.call(
                    normalized,
                    'include_in_client'
                ) === true

            if (hasMobileField || hasClientField) {
                normalized.include_in_mobile =
                    normalized.include_in_mobile !== false
                normalized.include_in_client =
                    normalized.include_in_client !== false
            } else {
                normalized.include_in_mobile = true
                normalized.include_in_client = true
            }

            return normalized
        },
        normalizeScheduledAtTimestamp(value) {
            if (!value) {
                return null
            }

            const date = value instanceof Date ? value : new Date(value)
            const timestamp = date.getTime()

            if (Number.isNaN(timestamp)) {
                return null
            }

            return timestamp
        },
        setScheduledAt(value) {
            const currentValue = this.modals.selectTarget.data.scheduled_at
            const currentTimestamp =
                this.normalizeScheduledAtTimestamp(currentValue)
            const nextTimestamp = this.normalizeScheduledAtTimestamp(value)

            if (currentTimestamp === nextTimestamp) {
                // Normalize legacy invalid non-null values to null.
                if (nextTimestamp === null && currentValue !== null) {
                    this.modals.selectTarget.data.scheduled_at = null
                }
                return
            }

            this.modals.selectTarget.data.scheduled_at =
                nextTimestamp === null ? null : new Date(nextTimestamp)
        },
        scheduleMinDate() {
            return new Date()
        },
        scheduleMinCalendarDate() {
            const now = this.scheduleMinDate()
            return new CalendarDate(
                now.getFullYear(),
                now.getMonth() + 1,
                now.getDate()
            )
        },
        scheduleTimeMinDate() {
            const value = this.modals.selectTarget.data.scheduled_at

            if (!value) {
                return null
            }

            const selectedDate = new Date(value)
            const now = new Date()
            const isToday =
                selectedDate.getFullYear() === now.getFullYear() &&
                selectedDate.getMonth() === now.getMonth() &&
                selectedDate.getDate() === now.getDate()

            return isToday ? now : null
        },
        clampScheduledAtToNow(candidateDate) {
            const now = new Date()
            if (candidateDate < now) {
                return now
            }

            return candidateDate
        },
        /**
         * Extract rollout data from a target's flat rollout_* fields
         * into the shape expected by the PercentageRollout component.
         */
        extractRolloutData(target) {
            if (!target) return null
            // Check for flat rollout fields from API
            if (target.rollout_status) {
                return {
                    mode: target.rollout_mode,
                    percentage: target.rollout_percentage,
                    status: target.rollout_status,
                    stage: target.rollout_stage,
                    started_at: target.rollout_started_at,
                    paused_at: target.rollout_paused_at,
                    completed_at: target.rollout_completed_at,
                    next_advance_at: target.rollout_next_advance_at,
                }
            }
            // Pending rollout config on draft/scheduled targets
            if (target.rollout_mode) {
                return {
                    mode: target.rollout_mode,
                    percentage:
                        target.rollout_percentage ??
                        (target.rollout_mode === 'automatic' ? 1 : null),
                    status: null,
                    stage: null,
                    started_at: null,
                    paused_at: null,
                    completed_at: null,
                    next_advance_at: null,
                }
            }
            // Fallback: support nested rollout.data payloads (legacy/test fixtures)
            if (target.rollout?.data) {
                return target.rollout.data
            }
            return null
        },
        onDragStart(_event) {
            this.dragging = true
        },
        onDragEnd(event) {
            this.dragging = false
            // oxlint-disable-next-line no-unused-vars
            const { newIndex, oldIndex } = event
            const rulesToUpdate = []
            for (let i = 0; i < this.contextRules.length; i++) {
                const rule = this.contextRules[i]
                rule.order = i + 1
                rulesToUpdate.push({
                    ulid: rule.ulid,
                    order: rule.order,
                })
            }

            TargetRuleService.reorder(
                this.currentEnvironment.projectKey,
                this.result.key,
                this.draftTarget.ulid,
                rulesToUpdate
            )
                .then(() => {
                    // Nothing to do
                })
                .catch((error) => {
                    errorMessage(error)
                })
        },
        confirmRemove() {
            this.removingFlag = true
            FlagService.remove(
                this.currentEnvironment.projectKey,
                this.modals.removeResult.key
            ).then(
                async () => {
                    this.sessionStore.removeFlag(this.modals.removeResult.key)
                    await this.goToFlagList()
                },
                (error) => {
                    this.removingFlag = false
                    this.modals.removeResult.error = error.message
                }
            )
        },
        confirmDraftTargetDelete() {
            this.modals.draftTargetDelete.processing = true
            const draftTargetUlid =
                this.draftTarget !== null
                    ? this.draftTarget.ulid
                    : this.hasDraftTargetUlid
            TargetService.delete(
                this.currentEnvironment.projectKey,
                this.currentEnvironment.environmentKey,
                draftTargetUlid
            )
                .then((_response) => {
                    this.draftTarget = null
                    this.hasDraftTargetUlid = null
                    this.stopDraftWatch()

                    this.modals.selectTarget.data.rollout =
                        this.extractRolloutData(this.draftTarget)
                    this.removeEditTargetHash()
                    this.targetValues = removeBy(
                        this.targetValues,
                        draftTargetUlid
                    )
                    // The rules on screen were the draft's. Show the live target's again.
                    this.refreshContextRules(this.currentTarget.ulid)
                    this.modals.draftTargetDelete.show = false
                    successMessage('Draft has been deleted')
                })
                .catch((error) => {
                    this.modals.draftTargetDelete.error = error.message
                })
                .finally(() => {
                    this.modals.draftTargetDelete.processing = false
                })
        },
        confirmScheduledTargetDelete() {
            TargetService.delete(
                this.currentEnvironment.projectKey,
                this.currentEnvironment.environmentKey,
                this.scheduledTarget.ulid
            )
                .then((_response) => {
                    this.targetValues = removeBy(
                        this.targetValues,
                        this.scheduledTarget.ulid
                    )
                    this.scheduledTarget = null
                    this.toggleScheduledTargetDeleteModal()
                })
                .catch((error) => {
                    this.modals.scheduledTargetDelete.error = error.message
                })
        },
        async confirmSelectTarget() {
            if (this.modals.selectTarget.processing) {
                return
            }

            this.modals.selectTarget.processing = true
            this.modals.selectTarget.error = null

            try {
                const publishPayload = {
                    ...this.modals.selectTarget.data,
                }
                const rolloutData = this.draftRolloutData
                publishPayload.rollout_enabled = rolloutData !== null
                if (rolloutData !== null) {
                    publishPayload.rollout_mode = rolloutData.mode
                    publishPayload.rollout_percentage =
                        rolloutData.mode === 'manual'
                            ? rolloutData.percentage
                            : null
                }
                delete publishPayload.rollout
                const isScheduled = !!publishPayload.scheduled_at

                const response = await TargetService.publish(
                    this.currentEnvironment.projectKey,
                    this.currentEnvironment.environmentKey,
                    this.draftTarget.ulid,
                    publishPayload
                )

                this.modals.selectTarget.show = false
                this.modals.selectTarget.error = null
                this.modals.selectTarget.confirmation = null
                this.modals.selectTarget.data.comment = null
                this.modals.selectTarget.data.scheduled_at = null

                this.draftTarget = null
                this.hasDraftTargetUlid = null
                this.stopDraftWatch()
                this.removeEditTargetHash()

                if (isScheduled) {
                    this.targetValues = upsertBy(
                        this.targetValues,
                        response.data
                    )
                    this.scheduledTarget = response.data
                } else {
                    this.applyPublishedTargets(
                        response.data,
                        response.meta?.expired_targets ?? []
                    )
                }
            } catch (error) {
                this.modals.selectTarget.error = error.message
            } finally {
                this.modals.selectTarget.processing = false
            }
        },
        submitFlagSettings() {
            if (
                this.can(this.PERMISSIONS.FLAG_UPDATE, {
                    projectUlid: this.project?.ulid,
                    environmentUlid: this.environment?.ulid,
                })
            ) {
                this.confirmUpdate()
            }
        },
        confirmUpdate() {
            this.modals.updateResult.isSaving = true

            const payload = {
                ...this.modals.updateResult.data,
            }

            FlagService.update(
                this.currentEnvironment.projectKey,
                this.modals.updateResult.data.key,
                payload
            ).then(
                (response) => {
                    const normalize =
                        this.normalizeFlagSettingsData ||
                        ((value) => JSON.parse(JSON.stringify(value || {})))

                    this.result = response.data
                    this.modals.updateResult.data = normalize(this.result)
                    this.modals.updateResult.isSaving = false
                    successMessage('Flag settings have been updated!')
                    this.sessionStore.upsertFlag(response.data)
                },
                (error) => {
                    this.modals.updateResult.error = error.message
                }
            )
        },
        confirmValueDefault() {
            if (this.editingLocked) return

            this.modals.valueDefault.isSaving = true
            FlagValueService.updateDefault(
                this.currentEnvironment.projectKey,
                this.selectedKey,
                this.modals.valueDefault.ulid
            )
                .then((response) => {
                    this.setValues(this.withDefaultValue(response.data))
                    successMessage('The new default value has been saved.')
                    this.modals.valueDefault.isSaving = false
                })
                .catch((error) => {
                    this.error = error
                })
        },
        createDraft() {
            // Targets are read-only while a swap runs, whatever calls this.
            if (this.loading.createDraft || this.editingLocked) {
                return
            }

            this.loading.createDraft = true

            TargetService.clone(
                this.currentEnvironment.projectKey,
                this.currentEnvironment.environmentKey,
                this.currentTarget.ulid
            )
                .then((response) => {
                    this.draftTarget = response.data
                    this.targetValues = upsertBy(
                        this.targetValues,
                        response.data
                    )
                    // The clone response includes the copied rules (?include=rules)
                    this.contextRules = response.data.rules?.data ?? []
                    this.startDraftWatch()
                    this.modals.selectTarget.selected = this.currentValue
                    this.modals.selectTarget.data.rollout =
                        this.extractRolloutData(this.draftTarget)
                    this.setEditTargetHash()
                })
                .catch((error) => {
                    this.error = error
                })
                .finally(() => {
                    this.loading.createDraft = false
                })
        },
        closeDraftMode() {
            this.draftTarget = null
            this.stopDraftWatch()
            this.modals.selectTarget.data.rollout = null
            this.removeEditTargetHash()
            this.refreshTarget()
        },
        loadFlag() {
            if (!this.currentEnvironment?.projectKey) {
                this.loading.flag = false
                return
            }

            this.loading.flag = true
            this.targetTabReady = false

            FlagService.get(
                this.currentEnvironment.projectKey,
                this.selectedKey
            )
                .then((response) => {
                    const normalize =
                        this.normalizeFlagSettingsData ||
                        ((value) => JSON.parse(JSON.stringify(value || {})))

                    this.result = response.data
                    this.modals.updateResult.data = normalize(this.result)
                    this.refreshFlagTargets()
                    this.refreshTarget()
                    this.refreshValues()
                    this.refreshSwap()
                })
                .catch((error) => {
                    this.error = error
                })
                .finally(() => (this.loading.flag = false))
        },
        openRemoveModal(key) {
            this.modals.removeResult.key = key
            this.toggleRemoveModal()
        },
        refreshContextRules(targetUlid) {
            this.loading.contextRule = true
            return TargetRuleService.list(
                this.currentEnvironment.projectKey,
                this.currentEnvironment.environmentKey,
                targetUlid
            )
                .then((response) => {
                    this.contextRules = response.data
                })
                .catch((error) => {
                    this.error = error
                })
                .finally(() => (this.loading.contextRule = false))
        },
        /**
         * Load the temporary swap (overlay) replacing this flag on the environment, if one is
         * still running. While it runs, the Target tab shows the swap's source environment
         * read-only instead of this environment's own targeting (ZEN-2026). Ships dark behind
         * the swaps flag, so nothing is requested while the flag is off.
         */
        async refreshSwap() {
            clearTimeout(this.swapExpiryTimer)
            this.swapExpiryTimer = null

            // Only the newest refresh may write swap state. An older one that finishes late is dropped,
            // so it can't bring back a swap that a newer response has already ended.
            const request = ++this.swapRequest
            // A refresh is for one flag on one environment. If the screen has moved to another by the time a
            // response lands, the response belongs to the old one and is dropped too.
            const { projectKey, environmentKey } = this.currentEnvironment
            const flagKey = this.selectedKey
            const flagUlid = this.result.ulid
            const isStale = () =>
                request !== this.swapRequest ||
                this.currentEnvironment.projectKey !== projectKey ||
                this.currentEnvironment.environmentKey !== environmentKey ||
                this.selectedKey !== flagKey

            try {
                const enabled = await isFeatureEnabled(
                    'enable-temporary-swaps',
                    false
                )
                if (isStale()) return
                if (!enabled) {
                    this.swap = null
                    this.environmentSwap = null
                    return
                }

                const response = await FlagSwapService.list(
                    projectKey,
                    environmentKey
                )
                if (isStale()) return
                const now = Date.now()
                const running = (response?.data ?? []).filter(
                    (item) => Date.parse(item.expires_at) > now
                )

                // A whole-environment swap is asked about directly rather than matched from the list: the
                // API says whether a snapshot serves this flag and which swap wins when several do, and a
                // flag the snapshot left out is simply not served by it.
                let swap = running.some((item) => item.snapshot)
                    ? await this.loadSnapshotSwap(
                          projectKey,
                          environmentKey,
                          flagKey
                      )
                    : null
                if (isStale()) return

                if (!swap) {
                    // An older swap only overrides values and names the flags it covers. The list is oldest
                    // first and a later swap wins a flag, so the last match is the one in effect.
                    const overlay = running
                        .filter(
                            (item) =>
                                !item.snapshot &&
                                (item.overrides ?? []).some(
                                    (override) =>
                                        override.flag_ulid === flagUlid
                                )
                        )
                        .at(-1)

                    swap = overlay
                        ? await this.loadValueOnlySwap(overlay)
                        : null
                    if (isStale()) return
                }

                // A whole-environment swap locks every flag here, so it is noted even when it leaves this
                // flag out. The list is oldest first, so the last one is the newest.
                const environmentSwap = this.environmentSwapFrom(
                    running.filter((item) => item.snapshot).at(-1)
                )

                this.swap = swap
                this.environmentSwap = environmentSwap
                // Drop a draft opened before the lock was known. Reloading the live target brings back its own rules.
                if (this.editingLocked && this.isDraft) this.closeDraftMode()

                const endsAt = [swap?.expiresAt, environmentSwap?.expiresAt]
                    .filter(Boolean)
                    .sort((a, b) => Date.parse(a) - Date.parse(b))[0]
                if (endsAt) this.scheduleSwapExpiry(endsAt)
            } catch (error) {
                // Keep the last known swap state: clearing it here would re-open editing mid-swap.
                if (!isStale()) errorMessage(error)
            }
        },
        /**
         * The whole-environment swap running on this environment, as the Target tab describes it
         * when the swap leaves the flag out. Only what the swap list carries: no request is made
         * for a flag the snapshot doesn't serve.
         *
         * @param {object|undefined} overlay The newest running swap that has a snapshot, from the swap list
         * @returns {object|null} The swap state, or null when no whole-environment swap runs
         */
        environmentSwapFrom(overlay) {
            if (!overlay) return null

            const sourceEnvironment = (
                this.project?.environments?.data ?? []
            ).find((item) => item.ulid === overlay.source_environment_ulid)

            return {
                expiresAt: overlay.expires_at,
                sourceName: sourceEnvironment?.name ?? 'another environment',
                snapshot: true,
                snapshotTakenAt:
                    overlay.snapshot?.taken_at ?? overlay.created_at ?? null,
            }
        },
        /**
         * What this environment serves for the flag while a whole-environment swap runs: the
         * source's targets, rules and rollout as they were when the swap was created (ZEN-2030).
         * The swap itself, with its end and snapshot times, comes back with them.
         *
         * @param {string} projectKey
         * @param {string} environmentKey
         * @param {string} flagKey
         * @returns {Promise<object|null>} The swap state the Target tab shows, or null when no snapshot swap serves this flag
         */
        async loadSnapshotSwap(projectKey, environmentKey, flagKey) {
            let response
            try {
                response = await FlagSwapService.served(
                    projectKey,
                    environmentKey,
                    flagKey
                )
            } catch (error) {
                // 404: no snapshot swap serves this flag. It was left out of the snapshot, a later swap
                // that only overrides values wins it, or the swap ended a moment ago.
                if (error?.response?.status === 404) return null
                throw error
            }
            const { overlay, source_environment: source } = response.data
            const targets = response.data.targets ?? []
            const target = pickLiveTarget(targets) ?? null
            const rolloutTarget =
                targets.find(
                    (item) =>
                        item.ulid !== target?.ulid &&
                        ['active', 'paused'].includes(item.rollout_status)
                ) ?? null

            return {
                expiresAt: overlay.expires_at,
                sourceName: source.name,
                snapshot: true,
                snapshotTakenAt:
                    overlay.snapshot?.taken_at ?? overlay.created_at ?? null,
                target,
                rules: target?.rules?.data ?? [],
                rollout: rolloutTarget
                    ? {
                          target: rolloutTarget,
                          rules: rolloutTarget.rules?.data ?? [],
                          percentage: rolloutTarget.rollout_percentage,
                          status: rolloutTarget.rollout_status,
                      }
                    : null,
            }
        },
        /**
         * What the Target tab shows for a swap that only overrides values: the source's live
         * target and rules, for reference (ZEN-2026).
         *
         * @param {object} overlay The running swap, from the swap list
         * @returns {Promise<object>} The swap state the Target tab shows
         */
        async loadValueOnlySwap(overlay) {
            const { projectKey } = this.currentEnvironment
            const sourceEnvironment = (
                this.project?.environments?.data ?? []
            ).find((item) => item.ulid === overlay.source_environment_ulid)

            let sourceTarget = null
            let sourceRules = []
            if (sourceEnvironment) {
                const targets = await TargetService.list(
                    projectKey,
                    sourceEnvironment.key,
                    this.selectedKey
                )
                sourceTarget = pickLiveTarget(targets.data) ?? null

                if (sourceTarget) {
                    const rules = await TargetRuleService.list(
                        projectKey,
                        sourceEnvironment.key,
                        sourceTarget.ulid
                    )
                    sourceRules = rules.data
                }
            }

            return {
                expiresAt: overlay.expires_at,
                sourceName: sourceEnvironment?.name ?? 'another environment',
                snapshot: false,
                target: sourceTarget,
                rules: sourceRules,
                rollout: null,
            }
        },
        /**
         * Re-check when the swap runs out, so the screen returns to the environment's own
         * targeting without a reload.
         *
         * @param {string} expiresAt ISO timestamp the swap ends
         */
        scheduleSwapExpiry(expiresAt) {
            const remaining = Date.parse(expiresAt) - Date.now()
            this.swapExpiryTimer = setTimeout(
                () => this.refreshSwap(),
                Math.min(remaining, MAX_TIMER_DELAY_MS)
            )
        },
        refreshFlagTargets() {
            this.loading.flagTargets = true
            FlagTargetService.list(
                this.currentEnvironment.projectKey,
                this.selectedKey
            )
                .then((response) => {
                    this.flagTargets = response.data
                })
                .catch((error) => {
                    this.error = error
                })
                .finally(() => (this.loading.flagTargets = false))
        },
        refreshHasDraftTargetUlid() {
            this.loading.hasDraftTargetUlid = true
            TargetService.listDrafts(
                this.currentEnvironment.projectKey,
                this.currentEnvironment.environmentKey,
                this.selectedKey
            )
                .then((response) => {
                    if (response.data && response.data.length > 0) {
                        this.hasDraftTargetUlid = response.data[0].ulid
                    } else {
                        this.hasDraftTargetUlid = null
                    }

                    // Auto-load draft if #edit-target hash is in the URL
                    if (this.autoLoadDraft) {
                        this.autoLoadDraft = false
                        if (this.hasDraftTargetUlid && !this.editingLocked) {
                            this.switchToDraft()
                        } else {
                            this.removeEditTargetHash()
                        }
                    }
                })
                .catch((error) => {
                    this.error = error
                })
                .finally(() => (this.loading.hasDraftTargetUlid = false))
        },
        refreshLastUse() {
            this.lastUse.loading = true
            UsageService.lastUse(
                this.currentEnvironment.projectKey,
                this.currentEnvironment.environmentKey,
                this.selectedKey
            )
                .then((response) => {
                    if (response !== undefined) {
                        this.lastUse.value = response.data ?? null
                    }
                })
                .catch((error) => {
                    this.error = error
                })
                .finally(() => {
                    this.lastUse.loading = false
                })
        },
        refreshUsageHistory() {
            this.usageHistory.loading = true
            this.usageHistory.error = null

            return UsageService.history(
                this.currentEnvironment.projectKey,
                this.currentEnvironment.environmentKey,
                this.selectedKey,
                this.usageHistory.period,
                this.usageHistory.compare
            )
                .then((response) => {
                    const payload = response?.data

                    if (!payload || !Array.isArray(payload.points)) {
                        this.usageHistory.data = {
                            points: [],
                            value_series: [],
                            totals: {
                                current: 0,
                                previous: null,
                                delta: null,
                                change_percent: null,
                            },
                        }
                        return
                    }

                    this.usageHistory.data = payload
                })
                .catch((_error) => {
                    this.usageHistory.error = 'Unable to load usage history'
                })
                .finally(() => {
                    this.usageHistory.loading = false
                })
        },
        usageSegmentsForPoint(point) {
            const segments = Array.isArray(point?.value_breakdown)
                ? [...point.value_breakdown]
                      .map((segment) => ({
                          ...segment,
                          count: Number(segment?.count || 0),
                      }))
                      .filter((segment) => segment.count > 0)
                : []

            if (segments.length > 0) {
                return segments
            }

            const count = Number(point?.count || 0)
            if (count <= 0) {
                return []
            }

            const fallbackSeries = this.usageHistorySeries?.[0]
            return [
                {
                    key: fallbackSeries?.key || '__total',
                    label: fallbackSeries?.label || 'Usage',
                    color: fallbackSeries?.color || '#22c55e',
                    count,
                },
            ]
        },
        usageAxisLabel(bucketStart) {
            if (!bucketStart) {
                return ''
            }

            const m = moment.utc(bucketStart).tz(this.resolveTimezone())

            if (!m.isValid()) {
                return ''
            }

            return this.usageHistory.period === '24h'
                ? m.format('h A')
                : m.format('MMM D')
        },
        usageTooltipLabel(point) {
            const bucket = point?.bucket_start
            if (!bucket) {
                return 'Unknown'
            }

            const m = moment.utc(bucket).tz(this.resolveTimezone())

            if (!m.isValid()) {
                return 'Unknown'
            }

            return this.usageHistory.period === '24h'
                ? m.format('MMM D, h A')
                : m.format('MMM D, YYYY')
        },
        historyTimestamp(target) {
            return (
                target.scheduled_at ||
                target.published_at ||
                target.expired_at ||
                target.created_at
            )
        },
        historyStatusLabel(target) {
            if (target.status === 'scheduled') {
                return 'Scheduled'
            }

            if (target.status === 'published') {
                return 'Published'
            }

            if (target.status === 'expired') {
                return 'Expired'
            }

            return 'Draft'
        },
        historyStatusClasses(target) {
            if (target.status === 'scheduled') {
                return 'bg-amber-50 text-amber-700 ring-amber-200'
            }

            if (target.status === 'published') {
                return 'bg-emerald-50 text-emerald-700 ring-emerald-200'
            }

            if (target.status === 'expired') {
                return 'bg-taupe-100 text-taupe-700 ring-taupe-200'
            }

            return 'bg-sky-50 text-sky-700 ring-sky-200'
        },
        historyValueLabel(target) {
            const display = target.value?.data?.display

            if (display === '' || display === null || display === undefined) {
                return '(empty)'
            }

            return this.result?.type === 'json'
                ? truncateDisplay(display)
                : display
        },
        historyValueFullText(target) {
            const display = target.value?.data?.display

            if (display === '' || display === null || display === undefined) {
                return '(empty)'
            }

            return display
        },
        isHistoryValueTruncated(target) {
            const display = target.value?.data?.display

            return (
                this.result?.type === 'json' &&
                typeof display === 'string' &&
                display.length > JSON_DISPLAY_MAX_LENGTH
            )
        },
        isEnvironmentTargetBlankString(target) {
            return (
                this.result?.type === 'string' &&
                target?.value?.data?.display === ''
            )
        },
        environmentTargetValueLabel(target) {
            const display = target?.value?.data?.display

            if (display === '' || display === null || display === undefined) {
                return '(empty)'
            }

            return this.result?.type === 'json'
                ? truncateDisplay(display)
                : display
        },
        valueOptionLabel(value) {
            const display = value?.display

            if (display === '' || display === null || display === undefined) {
                return '(empty)'
            }

            return this.result?.type === 'json'
                ? truncateDisplay(display)
                : display
        },
        // Full, untruncated value for the hover title — only needed when
        // the visible label may have been cut.
        environmentTargetValueTitle(target) {
            if (this.result?.type !== 'json') {
                return undefined
            }

            return target?.value?.data?.display ?? undefined
        },
        environmentTargetJsonLabel(target) {
            const rawValue = target?.value?.data?.value

            if (rawValue === null || rawValue === undefined) {
                return 'null'
            }

            if (typeof rawValue === 'object') {
                try {
                    return JSON.stringify(rawValue)
                } catch {
                    return String(rawValue)
                }
            }

            return String(rawValue)
        },
        historyChangeSummary(target) {
            const rolloutSummary = this.historyRolloutSummary(target)

            if (rolloutSummary !== 'No rollout') {
                return `${this.historyStatusLabel(target)} target update with ${rolloutSummary.toLowerCase()}`
            }

            return `${this.historyStatusLabel(target)} target update`
        },
        historyActor(target) {
            const firstName = target.user?.data?.first_name
            const lastName = target.user?.data?.last_name

            if (!firstName && !lastName) {
                return 'System'
            }

            return `${firstName || ''} ${lastName || ''}`.trim()
        },
        historyRolloutSummary(target) {
            if (target.rollout_status) {
                const mode = target.rollout_mode || 'manual'
                const percentage = target.rollout_percentage ?? 0
                const status =
                    target.rollout_status.charAt(0).toUpperCase() +
                    target.rollout_status.slice(1)

                return `${status} ${mode} rollout (${percentage}%)`
            }

            if (target.rollout_mode) {
                const mode = target.rollout_mode
                const percentage =
                    target.rollout_percentage ??
                    (mode === 'automatic' ? 1 : null)

                if (percentage === null) {
                    return `Configured ${mode} rollout`
                }

                return `Configured ${mode} rollout (${percentage}%)`
            }

            return 'No rollout'
        },
        historyRuleDescriptions(target) {
            return this.historyRules(target)
                .map((rule) => rule.description)
                .filter((description) => !!description)
        },
        historyRules(target) {
            return this.historyRulesByTarget[target.ulid] || []
        },
        historyRuleSummary(target) {
            const rules = this.historyRuleDescriptions(target)

            if (rules.length === 0) {
                return 'No rules'
            }

            return rules.length === 1 ? '1 rule' : `${rules.length} rules`
        },
        historyRulePreview(target) {
            const rules = this.historyRuleDescriptions(target)

            if (rules.length === 0) {
                return 'No rules'
            }

            if (rules.length <= 2) {
                return rules.join(' | ')
            }

            return `${rules[0]} | ${rules[1]} +${rules.length - 2} more`
        },
        historyRuleTooltip(target) {
            const rules = this.historyRuleDescriptions(target)

            return rules.length === 0 ? 'No rules' : rules.join('\n')
        },
        historyHasRollout(target) {
            return !!target.rollout_status || !!target.rollout_mode
        },
        historyHasRules(target) {
            return this.historyRules(target).length > 0
        },
        historyRuleCriteria(rule) {
            return rule?.criteria?.data || []
        },
        historyCriterionDetail(criterion) {
            const selector = criterion.selector
            const subSelector = criterion.sub_selector
            const comparer = criterion.comparer

            const contextValues = (criterion.contexts?.data || [])
                .map((context) => context.identifier || context.name)
                .filter((value) => !!value)
            const segmentValues = (criterion.segments?.data || [])
                .map((segment) => segment.name)
                .filter((value) => !!value)
            const directValues = (criterion.values?.data || [])
                .map((value) => value.value)
                .filter((value) => value !== null && value !== undefined)

            const values = [
                ...new Set([
                    ...contextValues,
                    ...segmentValues,
                    ...directValues,
                ]),
            ]

            const selectorLabel = subSelector
                ? `${selector}.${subSelector}`
                : selector

            if (values.length === 0) {
                return `${selectorLabel} ${comparer}`
            }

            return `${selectorLabel} ${comparer} ${values.join(', ')}`
        },
        isHistoryExpanded(targetUlid) {
            return this.historyExpanded[targetUlid] === true
        },
        toggleHistoryExpanded(targetUlid) {
            this.historyExpanded = {
                ...this.historyExpanded,
                [targetUlid]: !this.isHistoryExpanded(targetUlid),
            }
        },
        isHistoryValueExpanded(targetUlid) {
            return this.historyValueExpanded[targetUlid] === true
        },
        toggleHistoryValueExpanded(targetUlid) {
            this.historyValueExpanded = {
                ...this.historyValueExpanded,
                [targetUlid]: !this.isHistoryValueExpanded(targetUlid),
            }
        },
        // sortedHistory is newest-first, so the entry right after this one is
        // the version it replaced — the diff's "previous" side. Returns null
        // for the oldest entry, meaning there's nothing to diff against.
        historyPreviousTarget(target) {
            const index = this.sortedHistory.findIndex(
                (candidate) => candidate.ulid === target.ulid
            )

            if (index === -1 || index === this.sortedHistory.length - 1) {
                return null
            }

            return this.sortedHistory[index + 1]
        },
        historyPreviousDisplay(target) {
            return (
                this.historyPreviousTarget(target)?.value?.data?.display ?? null
            )
        },
        // Rolling back to the entry that's already the most recent published
        // target would be a no-op publish, so it isn't offered as an option.
        // Targets are read-only while a swap runs, and a rollback publishes one.
        canRollbackToTarget(target) {
            return (
                !this.editingLocked &&
                target.status !== 'draft' &&
                target.ulid !== this.currentTarget?.ulid
            )
        },
        openRollbackModal(target) {
            this.modals.rollbackTarget.target = target
            this.modals.rollbackTarget.comment = `Rolled back to the version from ${this.formatDateTime(this.historyTimestamp(target))}`
            this.modals.rollbackTarget.confirmation = null
            this.modals.rollbackTarget.error = null
            this.modals.rollbackTarget.show = true
        },
        closeRollbackModal() {
            if (this.modals.rollbackTarget.processing) {
                return
            }

            this.modals.rollbackTarget.show = false
            this.modals.rollbackTarget.target = null
            this.modals.rollbackTarget.comment = null
            this.modals.rollbackTarget.confirmation = null
            this.modals.rollbackTarget.error = null
        },
        async confirmRollback() {
            if (
                this.modals.rollbackTarget.processing ||
                !this.modals.rollbackTarget.target
            ) {
                return
            }

            this.modals.rollbackTarget.processing = true
            this.modals.rollbackTarget.error = null

            try {
                const cloned = await TargetService.clone(
                    this.currentEnvironment.projectKey,
                    this.currentEnvironment.environmentKey,
                    this.modals.rollbackTarget.target.ulid
                )

                await TargetService.publish(
                    this.currentEnvironment.projectKey,
                    this.currentEnvironment.environmentKey,
                    cloned.data.ulid,
                    {
                        comment: this.modals.rollbackTarget.comment,
                        rollout_enabled: false,
                    }
                )

                this.modals.rollbackTarget.show = false
                this.modals.rollbackTarget.target = null
                this.modals.rollbackTarget.comment = null
                this.modals.rollbackTarget.confirmation = null

                await this.refreshTarget()
                this.refreshFlagTargets()
                this.refreshHistoryRules()
                successMessage('Rolled back to the selected version.')
            } catch (error) {
                this.modals.rollbackTarget.error = error.message
            } finally {
                this.modals.rollbackTarget.processing = false
            }
        },
        refreshHistoryRules() {
            if (
                !Array.isArray(this.targetValues) ||
                this.targetValues.length === 0
            ) {
                this.historyRulesByTarget = {}
                this.loading.historyRules = false
                return Promise.resolve()
            }

            const targets = this.targetValues.filter(
                (target) => target.status !== 'draft'
            )

            if (targets.length === 0) {
                this.historyRulesByTarget = {}
                this.loading.historyRules = false
                return Promise.resolve()
            }

            this.loading.historyRules = true

            return Promise.allSettled(
                targets.map((target) =>
                    TargetRuleService.list(
                        this.currentEnvironment.projectKey,
                        this.currentEnvironment.environmentKey,
                        target.ulid
                    )
                )
            )
                .then((results) => {
                    const byTarget = {}

                    results.forEach((result, index) => {
                        const target = targets[index]

                        if (result.status !== 'fulfilled') {
                            byTarget[target.ulid] = []
                            return
                        }

                        byTarget[target.ulid] = result.value?.data || []
                    })

                    this.historyRulesByTarget = byTarget
                })
                .finally(() => {
                    this.loading.historyRules = false
                })
        },
        refreshScheduledTarget() {
            this.loading.scheduledTarget = true
            TargetService.listScheduled(
                this.currentEnvironment.projectKey,
                this.currentEnvironment.environmentKey,
                this.selectedKey
            )
                .then((response) => {
                    if (response.data && response.data.length > 0) {
                        this.scheduledTarget = response.data[0]
                    } else {
                        this.scheduledTarget = null
                    }
                })
                .catch((error) => {
                    this.error = error
                })
                .finally(() => (this.loading.scheduledTarget = false))
        },
        refreshTarget() {
            this.loading.target = true
            return TargetService.list(
                this.currentEnvironment.projectKey,
                this.currentEnvironment.environmentKey,
                this.selectedKey
            )
                .then((response) => {
                    this.targetValues = response.data
                    this.applyCurrentTarget()
                    this.refreshContextRules(this.currentTarget.ulid)
                    this.refreshHasDraftTargetUlid()
                    this.refreshScheduledTarget()
                    this.refreshLastUse()
                    // If there's a rollout target in read-only mode, load its rules
                    if (this.rolloutTarget) {
                        this.refreshRolloutTargetRules(this.rolloutTarget.ulid)
                    } else {
                        this.rolloutTargetRules = []
                    }
                    // Re-fetch the draft target so rollout fields are up-to-date
                    if (this.isDraft && this.draftTarget) {
                        this.refreshDraftTarget()
                    }

                    if (this.tab === 'history') {
                        this.refreshHistoryRules()
                    }
                })
                .catch((error) => {
                    this.error = error
                })
                .finally(() => {
                    this.loading.target = false
                    // Only a loaded live value can be shown. A failed first load stays on the spinner.
                    this.targetTabReady = this.currentValue !== null
                })
        },
        /**
         * Derive the live target from the loaded targets. The first published
         * target without an active rollout is the current (fallback) target.
         */
        applyCurrentTarget() {
            this.currentTarget = pickLiveTarget(this.targetValues)
            this.currentValue = this.currentTarget.value.data
            this.markDraftStaleIfLiveChanged()
        },
        /**
         * Fold a publish response into the loaded state. The response has the
         * target that went live and every target it expired, so the target list
         * and this flag's environment rows update without a re-fetch.
         */
        applyPublishedTargets(target, expiredTargets) {
            this.mergeTargets(target, expiredTargets)

            this.applyCurrentTarget()
            if (this.rolloutTarget) {
                this.refreshRolloutTargetRules(this.rolloutTarget.ulid)
            } else {
                this.rolloutTargetRules = []
            }
        },
        /**
         * Put a target and the targets it expired into the loaded list and
         * this flag's environment rows.
         */
        mergeTargets(target, expiredTargets) {
            let targets = upsertBy(this.targetValues, target)
            for (const expired of expiredTargets) {
                targets = upsertBy(targets, expired)
            }
            this.targetValues = targets

            const expiredUlids = expiredTargets.map((t) => t.ulid)
            this.flagTargets = upsertBy(
                this.flagTargets.filter((t) => !expiredUlids.includes(t.ulid)),
                target
            )
        },
        /**
         * A rollout action returned its target. Complete also returns the
         * fallback target it expired. Fold them into the loaded state instead
         * of re-fetching the targets.
         */
        onRolloutUpdated(target, expiredTargets = []) {
            if (this.isDraft && this.draftTarget?.ulid === target.ulid) {
                this.draftTarget = target
                this.modals.selectTarget.data.rollout =
                    this.extractRolloutData(target)
                return
            }

            const completed = target.rollout_status === 'completed'
            const rolloutRules = this.rolloutTargetRules

            this.mergeTargets(target, expiredTargets)
            if (completed) {
                // The completed rollout target is now the live target, so its
                // rules are the live rules. It no longer has a rollout to show.
                this.contextRules = rolloutRules
                this.rolloutTargetRules = []
            }
            this.applyCurrentTarget()
        },
        /**
         * Re-fetch the draft target so its rollout_* fields are current.
         */
        refreshDraftTarget() {
            TargetService.get(
                this.currentEnvironment.projectKey,
                this.currentEnvironment.environmentKey,
                this.draftTarget.ulid
            )
                .then((response) => {
                    // This tab may have left the draft while the request was in
                    // flight. Reopening it here would show a published target as a draft.
                    if (!this.draftTarget) return

                    this.draftTarget = response.data
                    this.modals.selectTarget.data.rollout =
                        this.extractRolloutData(this.draftTarget)
                })
                .catch(() => {
                    // Silently ignore — draft target may have been published
                })
        },
        refreshRolloutTargetRules(targetUlid) {
            TargetRuleService.list(
                this.currentEnvironment.projectKey,
                this.currentEnvironment.environmentKey,
                targetUlid
            )
                .then((response) => {
                    this.rolloutTargetRules = response.data
                })
                .catch(() => {
                    this.rolloutTargetRules = []
                })
        },
        refreshValues() {
            this.loading.values = true
            FlagValueService.list(
                this.currentEnvironment.projectKey,
                this.selectedKey
            )
                .then((response) => {
                    this.setValues(response.data)
                })
                .catch((error) => {
                    this.error = error
                })
                .finally(() => (this.loading.values = false))
        },
        /**
         * Store the values and derive the state that depends on them: the
         * default value, and the false and true values for the target toggles.
         * Callers that change one value pass the updated list here, so no
         * second fetch is needed.
         *
         * @param {Array<object>} values
         */
        setValues(values) {
            this.values = values
            this.modals.valueDefault.ulid = values.find(
                (value) => value.default
            )?.ulid
            this.falseValue = values.find((value) => value.display === 'false')
            this.trueValue = values.find((value) => value.display === 'true')
        },
        /**
         * The values with `value` as the only default. The API returns the
         * new default, so the previous default is cleared here.
         *
         * @param {object} value The value the API marked as the default
         * @returns {Array<object>}
         */
        withDefaultValue(value) {
            return upsertBy(this.values, value).map((existing) =>
                existing.default && existing.ulid !== value.ulid
                    ? { ...existing, default: false }
                    : existing
            )
        },
        removeRule(ulid) {
            this.contextRules = removeBy(this.contextRules, ulid)
        },
        onDraftRolloutChange(rolloutData) {
            this.modals.selectTarget.data.rollout = rolloutData
        },
        resetUpdateModal() {
            const normalize =
                this.normalizeFlagSettingsData ||
                ((value) => JSON.parse(JSON.stringify(value || {})))

            this.modals.updateResult.data = normalize(this.result)
        },
        switchToDraft() {
            if (this.editingLocked) return

            this.loading.target = true
            // Hold the spinner until the draft's own rules arrive. Otherwise the
            // tab would show the live target's rules as if they were the draft's.
            this.targetTabReady = false
            TargetService.get(
                this.currentEnvironment.projectKey,
                this.currentEnvironment.environmentKey,
                this.hasDraftTargetUlid
            )
                .then((response) => {
                    this.draftTarget = response.data
                    this.startDraftWatch()
                    this.modals.selectTarget.selected =
                        this.draftTarget.value.data
                    this.modals.selectTarget.data.rollout =
                        this.extractRolloutData(this.draftTarget)
                    this.setEditTargetHash()
                    return this.refreshContextRules(this.draftTarget.ulid)
                })
                .catch((error) => {
                    this.error = error
                })
                .finally(() => {
                    this.loading.target = false
                    this.targetTabReady = this.currentValue !== null
                })
        },
        toggleCreateRule() {
            this.modals.createRule.show = !this.modals.createRule.show
            if (!this.modals.createRule.show) {
                this.modals.createRule.data.rule = null
            }
        },
        toggleRemoveModal() {
            this.modals.removeResult.show = !this.modals.removeResult.show
            if (!this.modals.removeResult.show) {
                this.modals.removeResult.key = null
                this.modals.removeResult.error = null
            }
        },
        toggleDraftTargetDeleteModal() {
            if (this.modals.draftTargetDelete.processing) {
                return
            }
            this.modals.draftTargetDelete.show =
                !this.modals.draftTargetDelete.show
        },
        toggleScheduledTargetDeleteModal() {
            this.modals.scheduledTargetDelete.show =
                !this.modals.scheduledTargetDelete.show
        },
        toggleSelectTargetModal() {
            if (this.modals.selectTarget.processing) {
                return
            }

            this.modals.selectTarget.show = !this.modals.selectTarget.show
            this.modals.selectTarget.confirmation = null
            if (!this.modals.selectTarget.show) {
                this.modals.selectTarget.error = null
                this.modals.selectTarget.data.comment = null
                this.modals.selectTarget.data.scheduled_at = null
                this.modals.selectTarget.processing = false
            }
        },
        setEditTargetHash() {
            if (globalThis.window.location.hash !== '#edit-target') {
                this.$router.replace({ ...this.$route, hash: '#edit-target' })
            }
        },
        removeEditTargetHash() {
            if (globalThis.window.location.hash === '#edit-target') {
                // When leaving draft mode, revert to the current tab hash
                const hash = this.tab === 'target' ? '' : `#${this.tab}`
                this.$router.replace({ ...this.$route, hash })
            }
        },
        toggleShowValueCreate() {
            // Values are read-only while a swap runs, whatever calls this.
            if (this.editingLocked) return

            this.showingCreateValue = !this.showingCreateValue
        },
        toggleViewScheduleChangeModal() {
            this.modals.viewScheduledChange.show =
                !this.modals.viewScheduledChange.show
        },
        getInitialTab() {
            const hash = globalThis.window.location.hash.replace('#', '')
            const validTabs = [
                'target',
                'usage',
                'values',
                'history',
                'settings',
            ]
            if (hash === 'edit-target') return 'target'
            if (validTabs.includes(hash)) return hash
            return 'target'
        },
        toggleTab(tab) {
            this.tab = tab
            this.updateTabHash(tab)

            switch (tab) {
                case 'history':
                    if (
                        !this.tabHistoryLoaded ||
                        this.targetValues.length === 0
                    ) {
                        Promise.resolve(this.refreshTarget()).finally(() => {
                            this.refreshHistoryRules()
                        })
                        break
                    }

                    this.refreshHistoryRules()
                    break
                case 'target':
                    if (!this.tabTargetLoaded) {
                        this.refreshValues()
                        this.refreshTarget()
                    }
                    break
                case 'usage':
                    this.refreshUsageHistory()
                    break
                case 'values':
                    if (!this.tabValuesLoaded) {
                        this.refreshValues()
                    }
                    break
            }
        },
        updateTabHash(tab) {
            // When in draft mode on the target tab, keep #edit-target
            if (tab === 'target' && this.isDraft) {
                this.setEditTargetHash()
                return
            }
            // For the default tab, use no hash (clean URL)
            const hash = tab === 'target' ? '' : `#${tab}`
            if (globalThis.window.location.hash !== hash) {
                this.$router.replace({ ...this.$route, hash })
            }
        },
        toggleTarget(value) {
            TargetValueService.update(
                this.currentEnvironment.projectKey,
                this.currentEnvironment.environmentKey,
                this.draftTarget.ulid,
                value.ulid
            )
                .then((response) => {
                    this.draftTarget = response.data
                    this.modals.selectTarget.selected = value
                })
                .catch((error) => {
                    this.error = error
                })
        },
        createRule(rule) {
            this.toggleCreateRule()
            this.contextRules = upsertBy(this.contextRules, rule)
        },
        updateRule(rule) {
            this.contextRules = upsertBy(this.contextRules, rule)
        },
        valueCreated(value) {
            this.setValues(upsertBy(this.values, value))
            this.showingCreateValue = false
        },
        valueCreatedCancelled() {
            this.showingCreateValue = false
        },
        valueRemoved(value) {
            this.setValues(removeBy(this.values, value.ulid))
        },
        valueUpdated(value) {
            this.setValues(upsertBy(this.values, value))
        },

        /**
         * Keep this flag in step with changes made through the Management API
         * or MCP on the shared account connection. A reconnect refetches
         * everything in case a change was missed while it was down.
         */
        subscribeToAccountEvents() {
            const accountEvents = useAccountEventsStore()

            this.accountEventsUnsubscribers = [
                ...TARGET_EVENTS.map((eventName) =>
                    accountEvents.on(eventName, (data) =>
                        this.onTargetEvent(data)
                    )
                ),
                ...TARGET_RULE_EVENTS.map((eventName) =>
                    accountEvents.on(eventName, (data) =>
                        this.onTargetRuleEvent(data)
                    )
                ),
                accountEvents.on('flag-updated', (data) => {
                    if (!this.isViewedFlag(data)) return

                    // The default badge comes from the values, so refresh them
                    // too when the default value changed.
                    if (data.defaultChanged) {
                        this.refreshValues()
                    }

                    // Our own save echoes back before its response arrives. The
                    // response already updates the form, so skip the echo.
                    if (this.modals.updateResult.isSaving) return

                    this.syncFlag()
                }),
                accountEvents.on('flag-destroyed', (data) => {
                    if (!this.isViewedFlag(data) || this.removingFlag) return

                    infoMessage('This flag was deleted.')
                    this.goToFlagList()
                }),
                ...FLAG_VALUE_EVENTS.map((eventName) =>
                    accountEvents.on(eventName, (data) => {
                        if (this.isViewedFlag(data)) {
                            this.refreshValues()
                        }
                    })
                ),
                accountEvents.on('flag-swap-ended', (data) =>
                    this.onSwapEnded(data)
                ),
                accountEvents.onResync(() => {
                    if (this.selectedKey && this.currentEnvironment) {
                        this.refreshTarget()
                        this.refreshFlagTargets()
                        this.syncFlag()
                        this.refreshValues()
                        this.refreshSwap()
                    }
                }),
            ]
        },
        /**
         * A swap on an environment was reverted or ran out. When it was this
         * environment's, check what it serves now, so the Target tab goes back
         * to its own targeting without a reload (ZEN-2030).
         */
        onSwapEnded(data) {
            if (!this.selectedKey || !this.currentEnvironment) return
            if (
                data.projectKey !== this.currentEnvironment.projectKey ||
                data.environmentKey !== this.currentEnvironment.environmentKey
            ) {
                return
            }

            this.refreshSwap()
        },
        /**
         * A target for this flag changed. The current environment reloads its
         * target, which cascades to its rules, draft, and schedule. The
         * environment grid lists every environment, so it reloads for any
         * environment's change.
         */
        onTargetEvent(data) {
            if (!this.isViewedFlag(data)) return

            if (
                data.environmentKey === this.currentEnvironment.environmentKey
            ) {
                this.scheduleRealtimeRefresh(
                    `target:${data.environmentKey}`,
                    () => this.refreshTarget()
                )
            }

            this.onDraftEvent(data)
            this.scheduleRealtimeRefresh('flag-targets', () =>
                this.refreshFlagTargets()
            )
        },
        /**
         * A rule changed on a target. Reload the rules for whichever target
         * the screen shows. Other environments' rules are not on this screen.
         */
        onTargetRuleEvent(data) {
            if (!this.isViewedFlag(data)) return
            if (
                data.environmentKey !== this.currentEnvironment.environmentKey
            ) {
                return
            }

            this.onDraftEvent(data)

            const shownTargetUlid = this.isDraft
                ? this.draftTarget?.ulid
                : this.currentTarget?.ulid

            if (data.targetUlid === shownTargetUlid) {
                this.scheduleRealtimeRefresh(`rules:${data.targetUlid}`, () =>
                    this.refreshContextRules(data.targetUlid)
                )
            } else if (data.targetUlid === this.rolloutTarget?.ulid) {
                this.scheduleRealtimeRefresh(`rules:${data.targetUlid}`, () =>
                    this.refreshRolloutTargetRules(data.targetUlid)
                )
            } else {
                // A target this screen has not loaded yet, such as a draft
                // created elsewhere. Reloading the target picks it up.
                this.scheduleRealtimeRefresh(
                    `target:${data.environmentKey}`,
                    () => this.refreshTarget()
                )
            }
        },
        /**
         * The open draft may have changed. Echoes of this tab's own requests
         * are ignored. Anything else is checked against the server once the
         * burst of events settles, so the banner says what really happened.
         */
        onDraftEvent(data) {
            if (!this.isDraft || data.targetUlid !== this.draftTarget.ulid) {
                return
            }
            if (isOwnRequestId(data.requestId)) return

            this.scheduleRealtimeRefresh(`draft:${data.targetUlid}`, () =>
                this.checkDraftChanged(data)
            )
        },
        /**
         * Load the draft to learn what became of it. It is still a draft
         * (modified), or it was published or scheduled, or it was deleted. A
         * draft that is gone or expired was deleted.
         */
        checkDraftChanged(data) {
            const draftUlid = data.targetUlid

            return TargetService.get(
                this.currentEnvironment.projectKey,
                this.currentEnvironment.environmentKey,
                draftUlid
            )
                .then((response) => response.data.status)
                .catch((error) =>
                    error.response?.status === 404 ? 'expired' : null
                )
                .then(async (status) => {
                    // The screen may have left the draft while this loaded.
                    if (!this.isDraft || this.draftTarget.ulid !== draftUlid) {
                        return
                    }
                    // Any other failure says nothing, so keep the draft as it is.
                    if (!status) return

                    const how = await this.describeChange(data)

                    if (status === 'draft') {
                        this.draftChange = {
                            kind: 'modified',
                            text: `This draft was changed ${how}.`,
                            unsaved: [],
                        }
                        return
                    }

                    const outcome = {
                        published: 'published',
                        scheduled: 'scheduled',
                        expired: 'deleted',
                    }[status]

                    if (outcome) {
                        this.leaveChangedDraft(outcome, how)
                    }
                })
        },
        /**
         * Say who made a change, such as "via Management API by Jane". The
         * name comes from the account's users when the actor is one of them.
         */
        async describeChange(data) {
            const how =
                {
                    management_api: 'via Management API',
                    mcp_server: 'via the MCP server',
                    system: 'by a scheduled job',
                    web_app: 'in another browser tab',
                }[data.source] ?? 'elsewhere'

            if (!data.actorUlid) return how

            const users = await UserService.list()
                .then((body) => body.data ?? [])
                .catch(() => [])
            const actor = users.find((user) => user.ulid === data.actorUlid)

            return actor ? `${how} by ${actor.display_name}` : how
        },
        /**
         * Stop editing a draft that was published, scheduled, or deleted
         * elsewhere. The edits this tab had not saved are kept in the banner
         * so the user can copy them.
         */
        leaveChangedDraft(outcome, how) {
            const unsaved = this.unsavedDraftEdits()

            this.modals.selectTarget.show = false
            this.draftTarget = null
            this.hasDraftTargetUlid = null
            this.modals.selectTarget.data.rollout = null
            this.modals.selectTarget.data.scheduled_at = null
            this.modals.selectTarget.data.comment = null
            this.removeEditTargetHash()
            this.stopDraftWatch()
            this.draftChange = {
                kind: 'left',
                text: `This draft was ${outcome} ${how}. You are no longer editing it.`,
                unsaved,
            }
            this.refreshTarget()
        },
        /**
         * Edits in this tab to the open draft that the server does not have:
         * the schedule, the rollout, and the publish comment.
         */
        unsavedDraftEdits() {
            const local = this.modals.selectTarget.data
            const saved = this.draftTarget
            const edits = []

            if (
                (local.scheduled_at ?? null) !== (saved?.scheduled_at ?? null)
            ) {
                edits.push({
                    label: 'Schedule',
                    value: local.scheduled_at
                        ? this.formatDateTime(local.scheduled_at)
                        : 'Not scheduled',
                })
            }

            const savedRollout = this.rolloutSummary(
                this.extractRolloutData(saved)
            )
            const localRollout = this.rolloutSummary(local.rollout)
            if (localRollout !== savedRollout) {
                edits.push({
                    label: 'Rollout',
                    value: localRollout ?? 'No rollout',
                })
            }

            if (local.comment) {
                edits.push({ label: 'Comment', value: local.comment })
            }

            return edits
        },
        /** A short description of a rollout's mode and percentage, or null. */
        rolloutSummary(rollout) {
            if (!rollout?.mode) return null

            return rollout.percentage != null
                ? `${rollout.mode} ${rollout.percentage}%`
                : rollout.mode
        },
        /** Start watching the draft this tab just opened, against the live target now shown. */
        startDraftWatch() {
            this.draftBaseLiveUlid = this.currentTarget?.ulid ?? null
            this.draftBaseStale = false
            this.draftChange = null
        },
        /** Stop watching the draft, because this tab closed or published it. */
        stopDraftWatch() {
            this.draftBaseLiveUlid = null
            this.draftBaseStale = false
            this.draftChange = null
        },
        /** Flag the open draft when a different live target now serves the flag. */
        markDraftStaleIfLiveChanged() {
            if (!this.isDraft || !this.draftBaseLiveUlid) return

            if (this.currentTarget?.ulid !== this.draftBaseLiveUlid) {
                this.draftBaseStale = true
            }
        },
        /** Keep the draft as it is now and drop the notice about it. */
        dismissDraftChange() {
            this.draftChange = null
        },
        /** Load the draft as it is on the server, discarding what this tab showed. */
        reloadChangedDraft() {
            this.draftChange = null
            this.refreshDraftTarget()
        },
        /**
         * Run `refresh` once no event for `key` has arrived for a short while.
         * If the screen has moved to another flag or environment by then, the
         * refresh is dropped. Otherwise it would reload the new view because of
         * an event for the old one.
         */
        scheduleRealtimeRefresh(key, refresh) {
            const view = this.realtimeView()

            clearTimeout(this.realtimeRefreshTimers[key])
            this.realtimeRefreshTimers[key] = setTimeout(() => {
                delete this.realtimeRefreshTimers[key]
                if (this.isShowingView(view)) {
                    refresh()
                }
            }, REALTIME_REFRESH_DELAY_MS)
        },
        /** The project, environment, and flag this screen shows right now. */
        realtimeView() {
            return {
                projectKey: this.currentEnvironment.projectKey,
                environmentKey: this.currentEnvironment.environmentKey,
                flagKey: this.selectedKey,
            }
        },
        /** True when the screen still shows the given view. */
        isShowingView(view) {
            const current = this.realtimeView()

            return (
                current.projectKey === view.projectKey &&
                current.environmentKey === view.environmentKey &&
                current.flagKey === view.flagKey
            )
        },
        /** True when a flag event is for the flag this screen is showing. */
        isViewedFlag(data) {
            // Events can arrive before a project is loaded or after it is
            // cleared, so there is nothing to match against then.
            const projectKey = this.currentEnvironment?.projectKey
            return (
                Boolean(projectKey) &&
                data?.projectKey === projectKey &&
                data?.flagKey === this.selectedKey
            )
        },
        /**
         * Load the flag into the settings form, unless the form has unsaved
         * edits. Then keep the edits and offer a reload instead.
         */
        syncFlag() {
            if (this.hasUnsavedFlagSettings()) {
                this.flagUpdatedElsewhere = true
                return
            }

            this.refreshFlag()
        },
        hasUnsavedFlagSettings() {
            const normalize =
                this.normalizeFlagSettingsData ||
                ((value) => JSON.parse(JSON.stringify(value || {})))

            return (
                JSON.stringify(this.modals.updateResult.data) !==
                JSON.stringify(normalize(this.result))
            )
        },
        refreshFlag() {
            return FlagService.get(
                this.currentEnvironment.projectKey,
                this.selectedKey
            )
                .then((response) => {
                    const normalize =
                        this.normalizeFlagSettingsData ||
                        ((value) => JSON.parse(JSON.stringify(value || {})))

                    this.result = response.data
                    this.modals.updateResult.data = normalize(this.result)
                    this.flagUpdatedElsewhere = false
                })
                .catch((error) => {
                    errorMessage(error)
                })
        },
        /** Discard unsaved settings edits and show the latest flag. */
        reloadUpdatedFlag() {
            this.refreshFlag()
        },
        goToFlagList() {
            return this.$router.push({
                name: 'ProjectFlagsScreen',
                params: {
                    projectKey: this.currentEnvironment.projectKey,
                    environmentKey: this.currentEnvironment.environmentKey,
                },
            })
        },
        /**
         * Stop listening when the screen is destroyed. The shared connection
         * stays open for the other screens.
         */
        unsubscribeFromAccountEvents() {
            this.accountEventsUnsubscribers.forEach((unsubscribe) =>
                unsubscribe()
            )
            this.accountEventsUnsubscribers = []

            Object.values(this.realtimeRefreshTimers).forEach(clearTimeout)
            this.realtimeRefreshTimers = {}
        },
    },
    mounted() {
        if (globalThis.window.location.hash === '#edit-target') {
            this.autoLoadDraft = true
        }
        this.loadFlag()
        this.loadJsonUiFeatureFlag()
        this.subscribeToAccountEvents()

        // Trigger data loading for the initial tab if not the default
        if (this.tab !== 'target') {
            this.toggleTab(this.tab)
        }
    },
    beforeUnmount() {
        this.unsubscribeFromAccountEvents()
        clearTimeout(this.swapExpiryTimer)
    },
}
</script>

<style scoped>
/* Dragging state styles */
.dragging-active .drag-item-wrapper {
    position: relative;
}

/* Add visible drop zone indicator when dragging */
.dragging-active .drag-item-wrapper::before {
    content: '';
    position: absolute;
    top: -8px;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #0ea5e9 0%, #38bdf8 100%);
    border-radius: 2px;
    opacity: 0.5;
    transition: opacity 0.2s ease;
}

.dragging-active .drag-item-wrapper:hover::before {
    opacity: 1;
    height: 6px;
    top: -10px;
}

/* Style for the item being dragged */
.dragging-item {
    opacity: 0.5;
    transform: rotate(2deg);
    transition: transform 0.2s ease;
}

/* Style for the ghost/placeholder */
.dragging-ghost {
    opacity: 0.3;
    background: #e0f2fe;
    border: 2px dashed #0ea5e9;
    border-radius: 0.5rem;
}

/* First item gets a top indicator too */
.dragging-active .drag-item-wrapper:first-child::after {
    content: '';
    position: absolute;
    top: -8px;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #0ea5e9 0%, #38bdf8 100%);
    border-radius: 2px;
    opacity: 0.5;
}

.dragging-active .drag-item-wrapper:first-child:hover::after {
    opacity: 1;
    height: 6px;
    top: -10px;
}
</style>
